import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getItems, FIG_LISTS, getMarketConditions, getFromFIGVault, ensureFIGVault } from '../../api/figApi';
import { getAgencySettings } from '../../utils/invoiceSettings';
import JarvisChat from '../../centcom/JarvisChat';
import PolicySelector from './PolicySelector';
import InvoiceBuilder from './InvoiceBuilder';
import CockpitPolicyEditor from './CockpitPolicyEditor';
import { getPolicyHealth } from '../../utils/policyHealth';
import { buildVaultPath, statusToDocType } from '../../utils/buildVaultPath';
import { parseLocalDate } from '../../utils/parseLocalDate';
import './FIG.css';

// Touch-device detection — disable tooltips on touch (tap already opens detail)
const IS_TOUCH = typeof window !== 'undefined' && window.matchMedia?.('(hover: none)')?.matches;

// Month names for full date formatting
const MONTH_FULL_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function titleToFullDate(title) {
  // "2025-04" → "April 2025"
  if (!title) return '';
  const parts = String(title).split('-');
  if (parts.length !== 2) return title;
  const m = parseInt(parts[1], 10);
  return `${MONTH_FULL_NAMES[m - 1] || parts[1]} ${parts[0]}`;
}

function titleToQuarter(title) {
  // "2025-04" → "Q2 2025"
  if (!title) return '';
  const parts = String(title).split('-');
  if (parts.length !== 2) return title;
  const m = parseInt(parts[1], 10);
  const q = Math.ceil(m / 3);
  return `Q${q} ${parts[0]}`;
}

/* ========================================================================
   MarketTooltip — portal-based tooltip with viewport-aware positioning
   ======================================================================== */

function MarketTooltip({ targetRef, show, marketType = 'Admitted', children }) {
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!show || !targetRef.current) { setPos(null); return; }
    const r = targetRef.current.getBoundingClientRect();
    const margin = 12;
    const tooltipH = 240; // estimate for flip decision
    const viewportW = window.innerWidth;

    let placement = 'top';
    let top = r.top - margin;
    if (r.top - tooltipH - margin < 0) {
      placement = 'bottom';
      top = r.bottom + margin;
    }
    let left = r.left + r.width / 2;
    // Clamp — tooltip is up to 320px wide, or 90vw on narrow screens
    const halfW = Math.min(160, viewportW * 0.45);
    if (left < halfW + margin) left = halfW + margin;
    if (left > viewportW - halfW - margin) left = viewportW - halfW - margin;

    setPos({ top, left, placement });
  }, [show, targetRef]);

  if (!show || !pos) return null;
  return createPortal(
    <div className={`FIG-tooltip FIG-tooltip--${pos.placement} FIG-tooltip--${marketType.toLowerCase()}`}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        transform: pos.placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
      }}>
      {children}
    </div>,
    document.body
  );
}

function formatDistLine(pct, label) {
  return `${pct}% ${label}`;
}

/* ========================================================================
   DistributionBrief — click-triggered flight-brief popup for dist bars
   ======================================================================== */

function distributionNarrative(pct) {
  if (pct >= 75) return 'Strong positioning — majority of market favoring buyers';
  if (pct >= 50) return 'Mixed market — more accounts favorable than not';
  if (pct >= 30) return 'Challenging market — majority seeing increases';
  return 'Hard market — significant rate pressure across the board';
}

function primaryCalloutColor(pct) {
  if (pct >= 50) return '#22c55e';
  if (pct >= 30) return '#f59e0b';
  return '#ef4444';
}

// Segment metadata — index 0-3 maps to { color, callout, narrative, label }
const DIST_SEGMENTS = [
  { field: 'DistReductionFlat', color: '#22c55e',
    callout: (pct) => `${pct}% of renewals came in flat or lower`,
    short: (pct) => `${pct}% came in flat or lower`,
    narrative: 'This represents accounts with no rate increase or a decrease' },
  { field: 'Dist1to9', color: '#eab308',
    callout: (pct) => `${pct}% saw modest increases (1-9%)`,
    short: (pct) => `${pct}% saw modest increases (1-9%)`,
    narrative: 'This represents accounts with modest single-digit increases' },
  { field: 'Dist10to19', color: '#cc5544',
    callout: (pct) => `${pct}% saw significant increases (10-19%)`,
    short: (pct) => `${pct}% saw significant increases (10-19%)`,
    narrative: 'This represents accounts with significant double-digit increases' },
  { field: 'Dist20Plus', color: '#ff4444',
    callout: (pct) => `${pct}% saw severe increases (20%+)`,
    short: (pct) => `${pct}% saw severe increases (20%+)`,
    narrative: 'This represents accounts experiencing severe rate pressure' },
];

function DistributionBrief({ targetRef, show, distData, segmentIndex }) {
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!show || !targetRef?.current) { setPos(null); return; }
    const r = targetRef.current.getBoundingClientRect();
    const margin = 12;
    const tooltipH = 220;
    const viewportW = window.innerWidth;

    let placement = 'top';
    let top = r.top - margin;
    if (r.top - tooltipH - margin < 0) {
      placement = 'bottom';
      top = r.bottom + margin;
    }
    let left = r.left + r.width / 2;
    // Clamp — brief is up to 320px wide, or 90vw on narrow screens
    const halfW = Math.min(140, viewportW * 0.45);
    if (left < halfW + margin) left = halfW + margin;
    if (left > viewportW - halfW - margin) left = viewportW - halfW - margin;

    setPos({ top, left, placement });
  }, [show, targetRef]);

  if (!show || !pos || !distData) return null;

  const vals = DIST_SEGMENTS.map(s => Number(distData[s.field]) || 0);

  // Which segment to feature: explicit selection OR fallback to threshold logic on the flat bucket
  const hasSelection = typeof segmentIndex === 'number' && segmentIndex >= 0 && segmentIndex <= 3;
  const featIdx = hasSelection ? segmentIndex : 0;
  const featSeg = DIST_SEGMENTS[featIdx];
  const featVal = vals[featIdx];

  // Callout color: segment color when selected, threshold color when fallback
  const calloutColor = hasSelection ? featSeg.color : primaryCalloutColor(vals[0]);
  const calloutText = hasSelection ? featSeg.callout(featVal) : `${vals[0]}% of renewals came in flat or lower`;
  const narrativeText = hasSelection ? featSeg.narrative : distributionNarrative(vals[0]);

  return createPortal(
    <div className={`FIG-dist-brief FIG-dist-brief--${pos.placement}`}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        transform: pos.placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
      }}>
      <div className="FIG-dist-brief-header-stripe" />
      <div className="FIG-dist-brief-title">MARKET DISTRIBUTION BRIEF</div>
      <div className="FIG-dist-brief-primary" style={{ color: calloutColor }}>
        {calloutText}
      </div>
      <div className="FIG-dist-brief-secondary">
        {DIST_SEGMENTS.map((seg, i) => {
          const isSelected = hasSelection && i === featIdx;
          return (
            <div key={i} className={'FIG-dist-brief-line' + (isSelected ? ' FIG-dist-brief-line--selected' : '')}>
              <span className={'FIG-dist-brief-dot' + (isSelected ? ' FIG-dist-brief-dot--selected' : '')}
                style={{
                  background: seg.color,
                  ...(isSelected ? { boxShadow: `0 0 0 2px rgba(255,255,255,0.8), 0 0 8px ${seg.color}` } : {}),
                }} />
              <span>{seg.short(vals[i])}</span>
            </div>
          );
        })}
      </div>
      <div className="FIG-dist-brief-narrative">{narrativeText}</div>
    </div>,
    document.body
  );
}

// Build structured policy context for JBB and Claude prompt builder
function buildPolicyContext(policy, insuredMap) {
  if (!policy) return null;
  const ins = insuredMap?.[String(policy.InsuredId)];
  const days = policy.daysUntilExp ?? 0;
  const fmtD = (d) => {
    if (!d) return '—';
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
    if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
    const us = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(d);
    if (us) return d.slice(0, 10);
    return d;
  };
  return {
    client: ins?.CompanyName || 'Unknown',
    policyNumber: policy.PolicyNumber || '—',
    carrier: policy.Carrier || '—',
    lob: policy.LOB || '—',
    effectiveDate: fmtD(policy.EffectiveDate),
    expirationDate: fmtD(policy.ExpirationDate),
    daysRemaining: days,
    writtenPremium: policy.Premium ? '$' + Number(policy.Premium).toLocaleString() : '—',
    status: policy.Status || 'Active',
  };
}

function formatCurrency(n) {
  if (!n || n === 0) return '$0';
  if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + Math.round(n / 1000) + 'K';
  return '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatCurrencyFull(n) {
  if (!n || n === 0) return '$0';
  return '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function lerpColor(a, b, t) {
  const pa = [parseInt(a.slice(1, 3), 16), parseInt(a.slice(3, 5), 16), parseInt(a.slice(5, 7), 16)];
  const pb = [parseInt(b.slice(1, 3), 16), parseInt(b.slice(3, 5), 16), parseInt(b.slice(5, 7), 16)];
  return `rgb(${Math.round(pa[0] + (pb[0] - pa[0]) * t)},${Math.round(pa[1] + (pb[1] - pa[1]) * t)},${Math.round(pa[2] + (pb[2] - pa[2]) * t)})`;
}

function barColor(days) {
  if (days <= 30) return '#c0392b';
  if (days <= 60) return lerpColor('#c0392b', '#b8860b', (days - 30) / 30);
  if (days <= 90) return '#b8860b';
  if (days <= 135) return lerpColor('#b8860b', '#2e7d32', (days - 90) / 45);
  return '#2e7d32';
}

// Group sorted policies by InsuredId
function groupByClient(policies, insuredMap) {
  const seen = new Map();
  const order = [];
  policies.forEach(p => {
    const key = String(p.InsuredId);
    if (!seen.has(key)) {
      const ins = insuredMap[key];
      seen.set(key, {
        key, ins,
        clientName: ins ? (ins.CompanyName || ins.Title || 'Unknown') : 'Unknown Client',
        policies: [], soonest: p.daysUntilExp,
      });
      order.push(seen.get(key));
    }
    seen.get(key).policies.push(p);
  });
  return order;
}

/* ========================================================================
   CESSNA 172 ANALOG GAUGE — SVG
   Classic round instrument: dark face, white markings, orange needle,
   riveted bezel, subtle glass reflection.
   ======================================================================== */

const G = { cx: 100, cy: 100, r: 82 }; // gauge geometry

// Arc from angle a1 to a2 (radians, clockwise from top = 0)
function arc(cx, cy, r, a1, a2) {
  const x1 = cx + r * Math.sin(a1), y1 = cy - r * Math.cos(a1);
  const x2 = cx + r * Math.sin(a2), y2 = cy - r * Math.cos(a2);
  const large = (a2 - a1) > Math.PI ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
}

// Tick at angle (radians), inner/outer radius
function tick(cx, cy, rInner, rOuter, angle) {
  return {
    x1: cx + rInner * Math.sin(angle), y1: cy - rInner * Math.cos(angle),
    x2: cx + rOuter * Math.sin(angle), y2: cy - rOuter * Math.cos(angle),
  };
}

// Label position at angle
function labelPos(cx, cy, r, angle) {
  return { x: cx + r * Math.sin(angle), y: cy - r * Math.cos(angle) };
}

/*
 * AnalogGauge: full-circle Cessna-style instrument
 *
 * Props:
 *   value      — current value
 *   min, max   — scale range
 *   label      — text at bottom of dial face (e.g. "NEXT EXP")
 *   display    — text shown at center (below needle hub)
 *   subtext    — small text below display
 *   zones      — array of { from, to, color } for colored arcs
 *   ticks      — number of major tick divisions
 *   size       — 'lg' | 'sm'
 *   formatTick — optional fn(value) → string for tick labels
 */
// Phillips-head screw SVG at (sx, sy), radius sr
function PhillipsScrew({ sx, sy, sr = 3.2 }) {
  return (
    <g>
      {/* Screw body */}
      <circle cx={sx} cy={sy} r={sr} fill="#3a3a3a" stroke="#222" strokeWidth="0.4" />
      {/* Raised dome highlight */}
      <circle cx={sx - sr * 0.15} cy={sy - sr * 0.15} r={sr * 0.6} fill="#4a4a4a" opacity="0.5" />
      {/* Phillips cross slots */}
      <line x1={sx - sr * 0.6} y1={sy} x2={sx + sr * 0.6} y2={sy} stroke="#1a1a1a" strokeWidth="0.7" />
      <line x1={sx} y1={sy - sr * 0.6} x2={sx} y2={sy + sr * 0.6} stroke="#1a1a1a" strokeWidth="0.7" />
      {/* Rim catch light */}
      <circle cx={sx} cy={sy} r={sr} fill="none" stroke="#555" strokeWidth="0.3" strokeDasharray="2 4" />
    </g>
  );
}

const MONTH_NAMES = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
const MONTH_FULL = ['JANUARY','FEBRUARY','MARCH','APRIL','MAY','JUNE','JULY','AUGUST','SEPTEMBER','OCTOBER','NOVEMBER','DECEMBER'];

function AnalogGauge({ value, min, max, label, display, subtext, zones, ticks = 10, size = 'sm', formatTick,
  /* Month tick labels (replaces numeric ticks on large gauges) */
  monthTicks,
  /* Needle spin-up animation delay in ms */
  animDelay = 0,
  /* Nameplate marquee phase offset in ms (negative = pre-started for stagger) */
  marqueeDelay = 0,
  /* Enhanced interior (NEXT EXP + GREEN SPAN) */
  monthDisplay, ledValue, ledColor, ledPulse }) {
  const { cx, cy, r } = G;
  const startAngle = (225 / 180) * Math.PI;
  const sweep = (270 / 180) * Math.PI;

  const clamped = Math.max(min, Math.min(max, value));
  const frac = (clamped - min) / (max - min);
  const targetRotDeg = frac * sweep * (180 / Math.PI);

  // Needle spin-up animation
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), animDelay);
    return () => clearTimeout(t);
  }, [animDelay]);

  // LED count-up animation — starts after needle settles
  const ledTarget = ledValue ? Number(String(ledValue).replace(/[^0-9.-]/g, '')) : 0;
  const [ledCount, setLedCount] = useState(0);
  const [ledCounting, setLedCounting] = useState(false);
  const ledCountRef = useRef(null);

  useEffect(() => {
    if (!ledValue || ledTarget === 0) return;
    // Start counting after needle animation finishes (animDelay + 2s for needle swing)
    const startDelay = animDelay + 2000;
    const startTimer = setTimeout(() => {
      setLedCounting(true);
      const duration = 1800; // ms
      const startTime = performance.now();
      function step(now) {
        const elapsed = now - startTime;
        const progress = Math.min(1, elapsed / duration);
        // Ease-out with slight jumps: use exponential + quantize
        const eased = 1 - Math.pow(1 - progress, 3);
        // Quantize to create jumpy/mechanical steps (fewer steps near start, finer near end)
        const stepSize = Math.max(1, Math.round((1 - eased) * ledTarget * 0.08));
        const raw = eased * ledTarget;
        const quantized = Math.round(raw / stepSize) * stepSize;
        setLedCount(Math.min(quantized, ledTarget));
        if (progress < 1) {
          ledCountRef.current = requestAnimationFrame(step);
        } else {
          setLedCount(ledTarget);
          setLedCounting(false);
        }
      }
      ledCountRef.current = requestAnimationFrame(step);
    }, startDelay);
    return () => {
      clearTimeout(startTimer);
      if (ledCountRef.current) cancelAnimationFrame(ledCountRef.current);
    };
  }, [ledValue, ledTarget, animDelay]);

  const ledDisplayText = ledValue
    ? (ledCount === 0 && !ledCounting ? '$0' : '$' + Math.round(ledCount).toLocaleString())
    : null;

  // Draw needle at rest position (min/startAngle), rotate group to target
  const nl = size === 'lg' ? r - 10 : r - 12;
  const restNx = cx + nl * Math.sin(startAngle);
  const restNy = cy - nl * Math.cos(startAngle);
  const tailLen = 12;
  const restTx = cx - tailLen * Math.sin(startAngle);
  const restTy = cy + tailLen * Math.cos(startAngle);

  const zoneArcs = (zones || []).map((z, i) => {
    const f1 = Math.max(0, (z.from - min) / (max - min));
    const f2 = Math.min(1, (z.to - min) / (max - min));
    const a1 = startAngle + f1 * sweep;
    const a2 = startAngle + f2 * sweep;
    return <path key={i} d={arc(cx, cy, r - 6, a1, a2)} fill="none" stroke={z.color} strokeWidth="8" opacity="0.75" />;
  });

  const tickEls = [];
  for (let i = 0; i <= ticks; i++) {
    const f = i / ticks;
    const a = startAngle + f * sweep;
    const major = tick(cx, cy, r - 18, r - 2, a);
    tickEls.push(<line key={'t' + i} x1={major.x1} y1={major.y1} x2={major.x2} y2={major.y2}
      stroke="#ccc" strokeWidth="1.5" />);
    // Month labels or numeric labels
    const lp = labelPos(cx, cy, r - 26, a);
    const txt = monthTicks
      ? (i < monthTicks.length ? monthTicks[i] : '')
      : (formatTick ? formatTick(min + f * (max - min)) : Math.round(min + f * (max - min)).toString());
    const labelFontSize = monthTicks ? (size === 'lg' ? '6.5' : '5.5') : (size === 'lg' ? '8' : '7');
    if (txt) {
      tickEls.push(<text key={'l' + i} x={lp.x} y={lp.y} textAnchor="middle" dominantBaseline="central"
        fill="#999" fontSize={labelFontSize} fontFamily="'SF Mono','Consolas',monospace">{txt}</text>);
    }
    if (i < ticks) {
      const mf = (i + 0.5) / ticks;
      const ma = startAngle + mf * sweep;
      const minor = tick(cx, cy, r - 12, r - 2, ma);
      tickEls.push(<line key={'m' + i} x1={minor.x1} y1={minor.y1} x2={minor.x2} y2={minor.y2}
        stroke="#666" strokeWidth="0.8" />);
    }
  }

  const isLg = size === 'lg';
  const svgClass = isLg ? 'FIG-cessna-svg FIG-cessna-svg--lg' : 'FIG-cessna-svg';

  // Bezel screw positions — 3 Phillips screws around the bezel at 10:30, 2:30, 6:00
  const bezelScrewAngles = [(-45 / 180) * Math.PI, (225 / 180) * Math.PI, (90 / 180) * Math.PI];
  const bezelScrewR = 93;

  return (
    <svg viewBox="0 0 200 226" className={svgClass}>
      <defs>
        {/* Bezel chrome gradient — thick dimensional ring */}
        <linearGradient id="bezelChrome" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#888" />
          <stop offset="20%" stopColor="#555" />
          <stop offset="50%" stopColor="#333" />
          <stop offset="80%" stopColor="#444" />
          <stop offset="100%" stopColor="#666" />
        </linearGradient>
        {/* Bezel inner bevel shadow */}
        <radialGradient id="bezelInner" cx="50%" cy="50%" r="50%">
          <stop offset="88%" stopColor="transparent" />
          <stop offset="94%" stopColor="rgba(0,0,0,0.4)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.15)" />
        </radialGradient>
        {/* Glass reflection — curved arc highlight upper-left */}
        <radialGradient id="glassReflect" cx="35%" cy="30%" r="50%" fx="35%" fy="30%">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.18" />
          <stop offset="30%" stopColor="#fff" stopOpacity="0.06" />
          <stop offset="60%" stopColor="#fff" stopOpacity="0.01" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        {/* Glass edge rim highlight */}
        <radialGradient id="glassEdge" cx="50%" cy="50%" r="50%">
          <stop offset="85%" stopColor="transparent" />
          <stop offset="95%" stopColor="rgba(255,255,255,0.06)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.02)" />
        </radialGradient>
      </defs>

      {/* === BEZEL === */}
      {/* Outer bezel — thick chrome ring */}
      <circle cx={cx} cy={cy} r="98" fill="url(#bezelChrome)" />
      {/* Bezel knurling — fine lines for texture */}
      <circle cx={cx} cy={cy} r="97.5" fill="none" stroke="#3a3a3a" strokeWidth="0.3" />
      <circle cx={cx} cy={cy} r="95" fill="none" stroke="#2a2a2a" strokeWidth="0.5" />
      {/* Bezel inner edge — dark ring where glass meets metal */}
      <circle cx={cx} cy={cy} r="89" fill="none" stroke="#111" strokeWidth="1.5" />
      {/* Bezel inner shadow (recessed look) */}
      <circle cx={cx} cy={cy} r="90" fill="url(#bezelInner)" />

      {/* === DIAL FACE === */}
      <circle cx={cx} cy={cy} r={r + 6} fill="#111" />
      {/* Subtle radial texture on face */}
      <circle cx={cx} cy={cy} r={r + 5} fill="none" stroke="#1a1a1a" strokeWidth="0.5" />

      {/* Zone arcs */}
      {zoneArcs}

      {/* Tick marks + labels */}
      {tickEls}

      {/* === NEEDLE (animated rotation group) === */}
      <g style={{
        transform: `rotate(${animated ? targetRotDeg : 0}deg)`,
        transformOrigin: `${cx}px ${cy}px`,
        transition: animated ? 'transform 2.5s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none',
      }}>
        {/* Needle shadow (offset) */}
        <line x1={restTx + 1.5} y1={restTy + 1.5} x2={restNx + 1.5} y2={restNy + 1.5}
          stroke="#000" strokeWidth="3.5" strokeLinecap="round" opacity="0.3" />
        {/* Needle body — orange */}
        <line x1={restTx} y1={restTy} x2={restNx} y2={restNy} stroke="#e87400" strokeWidth="3" strokeLinecap="round" />
        {/* Needle center stripe — bright highlight */}
        <line x1={cx} y1={cy} x2={restNx} y2={restNy} stroke="#ffB060" strokeWidth="1" strokeLinecap="round" />
      </g>

      {/* Center hub — layered for depth */}
      <circle cx={cx} cy={cy} r="8" fill="#333" stroke="#555" strokeWidth="1.5" />
      <circle cx={cx} cy={cy} r="5" fill="#555" />
      <circle cx={cx} cy={cy} r="2.5" fill="#ddd" />

      {/* === DISPLAY === */}
      {monthDisplay ? (
        /* Enhanced interior — full month name + LOB + LED */
        <g>
          {/* Full month name — hero text in zone color */}
          <text x={cx} y={cy + 24} textAnchor="middle" fill={ledColor || '#44cc44'} fontSize="11" fontWeight="800"
            fontFamily="'SF Mono','Consolas',monospace" letterSpacing="1.5">{monthDisplay}</text>
          {/* LOB name */}
          {subtext && (
            <text x={cx} y={cy + 36} textAnchor="middle" fill="#666" fontSize="6"
              fontFamily="inherit">{subtext}</text>
          )}
          {/* LED digital readout panel */}
          {ledValue && (
            <g>
              {/* Recessed panel body */}
              <rect x={cx - 32} y={cy + 48} width="64" height="16" rx="3"
                fill="#0a0a0a" stroke="#333" strokeWidth="0.8" />
              {/* Inner bezel */}
              <rect x={cx - 31} y={cy + 49} width="62" height="14" rx="2.5"
                fill="#050505" stroke="#1a1a1a" strokeWidth="0.4" />
              {/* LED premium text — count-up animation */}
              <text x={cx} y={cy + 60} textAnchor="middle" fill={ledColor}
                fontSize="10" fontWeight="800" letterSpacing="0.5"
                fontFamily="'SF Mono','Consolas',monospace"
                className={
                  ledCounting ? 'FIG-led--counting' : (ledPulse || undefined)
                }>{ledDisplayText || '$0'}</text>
            </g>
          )}
        </g>
      ) : (
        /* Standard display */
        <g>
          <text x={cx} y={cy + 28} textAnchor="middle" fill="#fff" fontSize={isLg ? '18' : '15'} fontWeight="800"
            fontFamily="'SF Mono','Consolas',monospace">{display}</text>
          {subtext && (
            <text x={cx} y={cy + (isLg ? 42 : 40)} textAnchor="middle" fill="#777" fontSize={isLg ? '7' : '6'}
              fontFamily="inherit">{subtext}</text>
          )}
        </g>
      )}

      {/* === GLASS REFLECTION === */}
      <circle cx={cx} cy={cy} r={r + 5} fill="url(#glassReflect)" />
      <circle cx={cx} cy={cy} r={r + 5} fill="url(#glassEdge)" />

      {/* === BEZEL SCREWS — Phillips head === */}
      {bezelScrewAngles.map((a, i) => {
        const sx = cx + bezelScrewR * Math.sin(a);
        const sy = cy - bezelScrewR * Math.cos(a);
        return <PhillipsScrew key={'bs' + i} sx={sx} sy={sy} sr={2.8} />;
      })}

      {/* === ILLUMINATED LABEL PLATE === */}
      {/* Short labels (<10 chars) render static/centered. Long labels use a
         clipped two-copy marquee with breathing-ease CSS animation. */}
      <defs>
        <filter id={'glow-' + label.replace(/\s/g, '')}>
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        {String(label || '').length >= 10 && (
          <clipPath id={'plate-clip-' + String(label || '').replace(/\s/g, '') + '-' + Math.abs(String(label || '').split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) | 0, 7))}>
            <rect x={cx - 38} y="206" width="76" height="14" />
          </clipPath>
        )}
      </defs>
      {/* Plate body */}
      <rect x={cx - 40} y="204" width="80" height="18" rx="2"
        fill="#1a1a1a" stroke="#3a3a3a" strokeWidth="0.8" />
      {/* Inner bezel */}
      <rect x={cx - 39} y="205" width="78" height="16" rx="1.5"
        fill="#111" stroke="#2a2a2a" strokeWidth="0.4" />
      {/* Backlit glow bar behind text */}
      <rect x={cx - 30} y="208" width="60" height="10" rx="1"
        fill="#B8860B" opacity="0.06" />
      {/* Illuminated text — static if it fits, marquee if it overflows */}
      {(() => {
        const labelStr = String(label || '');
        const needsMarquee = labelStr.length >= 10;
        const glowUrl = 'url(#glow-' + label.replace(/\s/g, '') + ')';
        if (!needsMarquee) {
          return (
            <text x={cx} y="216.5" textAnchor="middle" fill="#d4aa3e"
              fontSize="8.5" fontWeight="800" letterSpacing="1.8"
              fontFamily="'SF Mono','Consolas',monospace"
              filter={glowUrl}>{label}</text>
          );
        }
        const _charW = 8.5 * 0.6;
        const _lspace = 1.8;
        const marqueeTextW = labelStr.length * (_charW + _lspace) - _lspace;
        const marqueeGap = 24;
        const marqueeStride = marqueeTextW + marqueeGap;
        const clipId = 'plate-clip-' + labelStr.replace(/\s/g, '') + '-' + Math.abs(labelStr.split('').reduce((a, c) => (a * 31 + c.charCodeAt(0)) | 0, 7));
        return (
          <g clipPath={`url(#${clipId})`}>
            <g
              className="FIG-plate-marquee"
              style={{
                '--fig-marquee-stride': `${marqueeStride.toFixed(2)}px`,
                animationDelay: `${marqueeDelay}ms`,
              }}
            >
              <text x={cx - 38} y="216.5" fill="#d4aa3e"
                fontSize="8.5" fontWeight="800" letterSpacing="1.8"
                fontFamily="'SF Mono','Consolas',monospace"
                filter={glowUrl}>{label}</text>
              <text x={cx - 38 + marqueeStride} y="216.5" fill="#d4aa3e"
                fontSize="8.5" fontWeight="800" letterSpacing="1.8"
                fontFamily="'SF Mono','Consolas',monospace"
                filter={glowUrl}>{label}</text>
            </g>
          </g>
        );
      })()}
    </svg>
  );
}

/* ========================================================================
   Market Readiness Dot — LED indicator on each gauge
   ======================================================================== */

function getMarketReadiness(policy) {
  const has3 = !!(policy.ThreeYearPriorCarrier && policy.ThreeYearPriorContact);
  const has2 = !!(policy.TwoYearPriorCarrier && policy.TwoYearPriorContact);
  const has1 = !!(policy.PriorCarrierName && policy.PriorCarrierContact);
  const hasCurrent = !!(policy.Carrier);
  const years = (hasCurrent ? 1 : 0) + (has1 ? 1 : 0) + (has2 ? 1 : 0) + (has3 ? 1 : 0);
  if (years >= 4 || (has3 && has2 && has1)) return { level: 'green', color: '#22c55e', label: 'Ready to shop', years };
  if (years >= 3 || (has2 && has1)) return { level: 'orange', color: '#f59e0b', label: 'Switching possible', years };
  return { level: 'red', color: '#ef4444', label: 'Stay & renew', years };
}

function MarketReadinessDot({ policy, isOpen, onToggle }) {
  const r = getMarketReadiness(policy);
  return (
    <div className="FIG-mkt-dot-wrap" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
      <div className={`FIG-mkt-dot FIG-mkt-dot--${r.level}`} title={r.label} />
      <div className="FIG-mkt-dot-label">MRI</div>
      {isOpen && (
        <div className="FIG-mkt-popover" onClick={(e) => e.stopPropagation()}>
          <div className="FIG-mkt-popover-title">CARRIER HISTORY</div>
          <div className="FIG-mkt-popover-status" style={{ color: r.color }}>{r.label}</div>
          <div className="FIG-mkt-popover-rows">
            <div className="FIG-mkt-popover-row">
              <span className={policy.Carrier ? 'FIG-mkt-check--yes' : 'FIG-mkt-check--no'}>
                {policy.Carrier ? '●' : '○'}
              </span>
              <span className="FIG-mkt-popover-label">Current</span>
              <span className="FIG-mkt-popover-val">{policy.Carrier || '—'}</span>
            </div>
            <div className="FIG-mkt-popover-row">
              <span className={policy.PriorCarrierName ? 'FIG-mkt-check--yes' : 'FIG-mkt-check--no'}>
                {policy.PriorCarrierName ? '●' : '○'}
              </span>
              <span className="FIG-mkt-popover-label">Prior</span>
              <span className="FIG-mkt-popover-val">{policy.PriorCarrierName || '—'}</span>
            </div>
            <div className="FIG-mkt-popover-row">
              <span className={policy.TwoYearPriorCarrier ? 'FIG-mkt-check--yes' : 'FIG-mkt-check--no'}>
                {policy.TwoYearPriorCarrier ? '●' : '○'}
              </span>
              <span className="FIG-mkt-popover-label">2yr back</span>
              <span className="FIG-mkt-popover-val">{policy.TwoYearPriorCarrier || '—'}</span>
            </div>
            <div className="FIG-mkt-popover-row">
              <span className={policy.ThreeYearPriorCarrier ? 'FIG-mkt-check--yes' : 'FIG-mkt-check--no'}>
                {policy.ThreeYearPriorCarrier ? '●' : '○'}
              </span>
              <span className="FIG-mkt-popover-label">3yr back</span>
              <span className="FIG-mkt-popover-val">{policy.ThreeYearPriorCarrier || '—'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========================================================================
   MSG (Message) Advisory Light — sibling of MRI on the policy gauge.
   Hover-driven HUD tooltip; messages stack via getPolicyHealth().
   ======================================================================== */

const MSG_ROW_RANK = { red: 0, yellow: 1 };

function MessageLight({ policy }) {
  const { level, messages } = getPolicyHealth(policy);
  const [show, setShow] = useState(false);
  const showTimerRef = useRef(null);

  function onEnter() {
    if (IS_TOUCH) return;
    showTimerRef.current = setTimeout(() => setShow(true), 200);
  }
  function onLeave() {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    setShow(false);
  }
  useEffect(() => () => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
  }, []);

  // Red rows above yellow rows — severity is the only sort key.
  const sorted = [...messages].sort(
    (a, b) => (MSG_ROW_RANK[a.level] ?? 9) - (MSG_ROW_RANK[b.level] ?? 9)
  );

  return (
    <div
      className="FIG-msg-dot-wrap"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div className={`FIG-msg-dot FIG-msg-dot--${level}`} />
      <div className="FIG-msg-dot-label">MSG</div>
      <div
        className={
          'FIG-msg-tooltip'
          + ` FIG-msg-tooltip--${level}`
          + (show ? ' FIG-msg-tooltip--show' : '')
        }
      >
        <div className="FIG-msg-tooltip-header">ADVISORY · MSG</div>
        {sorted.length === 0 ? (
          <div className="FIG-msg-tooltip-nominal">NOMINAL</div>
        ) : (
          <ul className="FIG-msg-tooltip-list">
            {sorted.map((m, i) => (
              <li
                key={i}
                className={`FIG-msg-tooltip-row FIG-msg-tooltip-row--${m.level}`}
              >
                <span className="FIG-msg-tooltip-row-dot">●</span>
                <span className="FIG-msg-tooltip-row-text">{m.reason}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ========================================================================
   Market Conditions Annunciator Tile
   ======================================================================== */

const LOB_LABELS_ADMITTED = {
  CommAuto: 'COMM AUTO', BOP: 'BOP', GL: 'GL',
  CommProperty: 'COMM PROP', Umbrella: 'UMBRELLA', WC: 'WC',
};
const LOB_ORDER_ADMITTED = ['CommAuto', 'BOP', 'GL', 'CommProperty', 'Umbrella', 'WC'];

const LOB_LABELS_SURPLUS = {
  Property: 'PROPERTY', ExcessUmbrella: 'EXCESS+UMB', Cyber: 'CYBER',
  EO: 'E&O', EPL: 'EPL', DO: 'D&O',
};
const LOB_ORDER_SURPLUS = ['Property', 'ExcessUmbrella', 'Cyber', 'EO', 'EPL', 'DO'];

// Aliases for backward compatibility
const LOB_LABELS = LOB_LABELS_ADMITTED;
const LOB_ORDER = LOB_ORDER_ADMITTED;

function tileColor(rate, direction, marketType) {
  if (marketType === 'Surplus') {
    if (rate < 0 || (direction === 'down' && rate < 4)) return '#44cc44';
    if (rate > 7 && direction === 'up') return '#ff4444';
    return '#cc3333';
  }
  if (rate < 0 || (direction === 'down' && rate < 4)) return '#44cc44';
  if (rate > 7 && direction === 'up') return '#ff4444';
  return '#e6a817';
}

// Map a policy's LOB + AdmittedStatus to a market-conditions entry.
// Returns { lob, type, entries } or null if not tracked.
const POLICY_LOB_MARKET_MAP = {
  'commercial general liability': { admitted: 'GL', surplus: null },
  'general liability': { admitted: 'GL', surplus: null },
  'commercial property': { admitted: 'CommProperty', surplus: 'Property' },
  'commercial auto': { admitted: 'CommAuto', surplus: null },
  'business owners policy': { admitted: 'BOP', surplus: null },
  'workers compensation': { admitted: 'WC', surplus: null },
  'umbrella': { admitted: 'Umbrella', surplus: 'ExcessUmbrella' },
  'excess liability': { admitted: 'Umbrella', surplus: 'ExcessUmbrella' },
  'cyber': { admitted: null, surplus: 'Cyber' },
  'errors and omissions': { admitted: null, surplus: 'EO' },
  'professional liability': { admitted: null, surplus: 'EO' },
  'employment practices liability': { admitted: null, surplus: 'EPL' },
  'directors and officers': { admitted: null, surplus: 'DO' },
  'd&o': { admitted: null, surplus: 'DO' },
};

function findMarketEntry(policy, marketAdmitted, marketSurplus) {
  if (!policy) return null;
  const lob = (policy.LOB || '').toLowerCase();
  const m = POLICY_LOB_MARKET_MAP[lob];
  if (!m) return null;
  const isSurplus = (policy.AdmittedStatus || '').toLowerCase().includes('surplus');

  if (isSurplus && m.surplus && marketSurplus[m.surplus]?.length) {
    return { lob: m.surplus, type: 'Surplus', entries: marketSurplus[m.surplus] };
  }
  if (m.admitted && marketAdmitted[m.admitted]?.length) {
    return { lob: m.admitted, type: 'Admitted', entries: marketAdmitted[m.admitted] };
  }
  if (m.surplus && marketSurplus[m.surplus]?.length) {
    return { lob: m.surplus, type: 'Surplus', entries: marketSurplus[m.surplus] };
  }
  return null;
}

function marketNarrative(rate) {
  if (rate < 0) return 'Market trending favorable — push for better terms at renewal';
  if (rate > 10) return 'Market hardening — prepare client for increase';
  return 'Market stable — maintain current positioning';
}

/* ========================================================================
   MarketTimeline — interactive line chart with drag-to-zoom range selector
   ======================================================================== */

function titleToShortLabel(title) {
  // "2025-04" → "Apr 25"
  if (!title) return '';
  const parts = String(title).split('-');
  if (parts.length !== 2) return title;
  const m = parseInt(parts[1], 10);
  const monthAbbr = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${monthAbbr[m - 1] || parts[1]} ${parts[0].slice(-2)}`;
}

function MarketTimeline({ entries, marketType }) {
  const n = entries.length;
  const MIN_RANGE = 3;
  const DEFAULT_WINDOW = 12;
  const initialStart = Math.max(0, n - DEFAULT_WINDOW);
  const initialEnd = n - 1;

  const [rangeStart, setRangeStart] = useState(initialStart);
  const [rangeEnd, setRangeEnd] = useState(initialEnd);
  const [dragging, setDragging] = useState(null); // 'left' | 'right'
  const [hoveredPoint, setHoveredPoint] = useState(null); // { idx, x, y, rate, title } | null
  const rangeBarRef = useRef(null);

  // Clear point tooltip when drag starts
  useEffect(() => { if (dragging) setHoveredPoint(null); }, [dragging]);

  // Touch device: tap elsewhere dismisses point tooltip
  useEffect(() => {
    if (!hoveredPoint || !IS_TOUCH) return;
    const handler = () => setHoveredPoint(null);
    const t = setTimeout(() => document.addEventListener('touchstart', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('touchstart', handler); };
  }, [hoveredPoint]);

  // Reset range when entries change (e.g., switching LOB)
  useEffect(() => {
    setRangeStart(Math.max(0, entries.length - DEFAULT_WINDOW));
    setRangeEnd(entries.length - 1);
  }, [entries]);

  // Drag handler
  useEffect(() => {
    if (!dragging || !rangeBarRef.current) return;
    const bar = rangeBarRef.current;

    function move(ev) {
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const rect = bar.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const idx = Math.round(pct * (n - 1));

      if (dragging === 'left') {
        setRangeStart(Math.max(0, Math.min(idx, rangeEnd - (MIN_RANGE - 1))));
      } else {
        setRangeEnd(Math.max(rangeStart + (MIN_RANGE - 1), Math.min(n - 1, idx)));
      }
    }

    function end() { setDragging(null); }

    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', end);
    document.addEventListener('touchmove', move, { passive: false });
    document.addEventListener('touchend', end);
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', end);
      document.removeEventListener('touchmove', move);
      document.removeEventListener('touchend', end);
    };
  }, [dragging, rangeStart, rangeEnd, n]);

  const visible = entries.slice(rangeStart, rangeEnd + 1);
  const isAdmitted = marketType === 'Admitted';
  const lineColor = isAdmitted ? '#e6a817' : '#cc3333';

  // Chart layout — fixed viewBox, scales via preserveAspectRatio
  const W = 800, H = 140;
  const PADX = 30, PADT = 20, PADB = 24;
  const innerW = W - PADX * 2;
  const innerH = H - PADT - PADB;

  // Y scale — always include 0
  let yMin = Math.min(...visible.map(e => Number(e.RateChange) || 0));
  let yMax = Math.max(...visible.map(e => Number(e.RateChange) || 0));
  if (yMin > 0) yMin = 0;
  if (yMax < 0) yMax = 0;
  if (yMin === yMax) yMax = yMin + 1;
  const ySpan = yMax - yMin;
  const yMap = v => PADT + (1 - (v - yMin) / ySpan) * innerH;
  const zeroY = yMap(0);

  // X positions
  const xAt = i => visible.length > 1 ? PADX + (i / (visible.length - 1)) * innerW : W / 2;

  // Line path
  const linePts = visible.map((e, i) => `${xAt(i)},${yMap(Number(e.RateChange) || 0)}`).join(' ');

  // Area path — closes to zero line
  const areaPath = visible.length > 0
    ? `M ${xAt(0)},${zeroY} L ${visible.map((e, i) => `${xAt(i)},${yMap(Number(e.RateChange) || 0)}`).join(' L ')} L ${xAt(visible.length - 1)},${zeroY} Z`
    : '';

  // Grid lines — 4 horizontal
  const gridYs = [yMin, yMin + ySpan * 0.25, yMin + ySpan * 0.5, yMin + ySpan * 0.75, yMax];

  // Month label density
  const labelSkip = Math.max(1, Math.ceil(visible.length / 8));

  // Range label
  const firstTitle = visible[0]?.Title;
  const lastTitle = visible[visible.length - 1]?.Title;
  const rangeLabel = firstTitle && lastTitle
    ? `${titleToFullDate(firstTitle)} — ${titleToFullDate(lastTitle)} (${visible.length} month${visible.length !== 1 ? 's' : ''})`
    : '';

  // Range bar positions as percentages
  const leftPct = n > 1 ? (rangeStart / (n - 1)) * 100 : 0;
  const rightPct = n > 1 ? (rangeEnd / (n - 1)) * 100 : 100;

  return (
    <div className="FIG-market-timeline">
      <div className="FIG-market-timeline-range-label">{rangeLabel}</div>
      <svg className="FIG-market-timeline-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        {/* Grid lines */}
        {gridYs.map((v, i) => (
          <line key={'g' + i} className="FIG-market-timeline-gridline"
            x1={PADX} x2={W - PADX} y1={yMap(v)} y2={yMap(v)} />
        ))}
        {/* Zero line */}
        <line className="FIG-market-timeline-zeroline"
          x1={PADX} x2={W - PADX} y1={zeroY} y2={zeroY} />
        {/* Area fill */}
        <path className="FIG-market-timeline-area" d={areaPath}
          fill={lineColor} fillOpacity="0.1" />
        {/* Main line */}
        {visible.length > 1 && (
          <polyline className="FIG-market-timeline-line" points={linePts}
            fill="none" stroke={lineColor} strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" />
        )}
        {/* Data points */}
        {visible.map((e, i) => {
          const r = Number(e.RateChange) || 0;
          const cx = xAt(i);
          const cy = yMap(r);
          function activatePoint(ev) {
            const rect = ev.currentTarget.getBoundingClientRect();
            setHoveredPoint({
              idx: i,
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2,
              rate: r,
              title: e.Title,
            });
          }
          return (
            <g key={e.id || i}>
              {/* Invisible hit target — larger for easier hover/tap */}
              <circle className="FIG-market-timeline-hit"
                cx={cx} cy={cy} r="14"
                fill="transparent"
                onMouseEnter={activatePoint}
                onMouseLeave={() => setHoveredPoint(null)}
                onTouchStart={(ev) => { ev.preventDefault(); ev.stopPropagation(); activatePoint(ev); }}
              />
              {/* Visible point */}
              <circle className="FIG-market-timeline-point"
                cx={cx} cy={cy} r="4"
                fill="#111" stroke={lineColor} strokeWidth="2"
                pointerEvents="none" />
              {/* Surplus mini dist bar under each point */}
              {!isAdmitted && e.DistReductionFlat != null && (() => {
                const segs = [
                  { val: Number(e.DistReductionFlat) || 0, color: '#22c55e' },
                  { val: Number(e.Dist1to9) || 0, color: '#eab308' },
                  { val: Number(e.Dist10to19) || 0, color: '#cc5544' },
                  { val: Number(e.Dist20Plus) || 0, color: '#ff4444' },
                ].filter(s => s.val > 0);
                const total = segs.reduce((s, x) => s + x.val, 0);
                const barW = Math.min(24, innerW / visible.length * 0.7);
                const barH = 4;
                const barY = H - PADB - 2;
                const barX = cx - barW / 2;
                let xoff = 0;
                return (
                  <g>
                    {segs.map((s, j) => {
                      const w = total > 0 ? (s.val / total) * barW : 0;
                      const el = <rect key={j} x={barX + xoff} y={barY}
                        width={w} height={barH} fill={s.color} />;
                      xoff += w;
                      return el;
                    })}
                  </g>
                );
              })()}
            </g>
          );
        })}
        {/* X-axis month labels */}
        {visible.map((e, i) => {
          if (i % labelSkip !== 0 && i !== visible.length - 1) return null;
          return (
            <text key={'l' + i} className="FIG-market-timeline-label"
              x={xAt(i)} y={H - 6} textAnchor="middle">
              {titleToShortLabel(e.Title)}
            </text>
          );
        })}
      </svg>
      {/* Range selector bar */}
      <div ref={rangeBarRef} className="FIG-market-timeline-rangebar">
        <div className="FIG-market-timeline-rangebar-track" />
        <div className="FIG-market-timeline-rangebar-selected"
          style={{
            left: `${leftPct}%`,
            width: `${rightPct - leftPct}%`,
            background: lineColor,
          }} />
        <div className={'FIG-market-timeline-handle FIG-market-timeline-handle--left'
            + (dragging === 'left' ? ' FIG-market-timeline-handle--active' : '')}
          style={{ left: `calc(${leftPct}% - 7px)`, background: lineColor }}
          onMouseDown={(e) => { e.preventDefault(); setDragging('left'); }}
          onTouchStart={(e) => { e.preventDefault(); setDragging('left'); }} />
        <div className={'FIG-market-timeline-handle FIG-market-timeline-handle--right'
            + (dragging === 'right' ? ' FIG-market-timeline-handle--active' : '')}
          style={{ left: `calc(${rightPct}% - 7px)`, background: lineColor }}
          onMouseDown={(e) => { e.preventDefault(); setDragging('right'); }}
          onTouchStart={(e) => { e.preventDefault(); setDragging('right'); }} />
      </div>
      {hoveredPoint && createPortal(
        <div className={`FIG-tl-point-tooltip FIG-tl-point-tooltip--${marketType.toLowerCase()}`}
          style={{
            position: 'fixed',
            top: hoveredPoint.y - 10,
            left: hoveredPoint.x,
            transform: 'translate(-50%, -100%)',
          }}>
          <div className="FIG-tl-point-tooltip-month">{titleToFullDate(hoveredPoint.title)}</div>
          <div className="FIG-tl-point-tooltip-rate" style={{ color: lineColor }}>
            {hoveredPoint.rate > 0 ? '+' : ''}{hoveredPoint.rate.toFixed(2)}%
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Tooltip content for a market tile (admitted or surplus)
function TileTooltipContent({ lob, label, marketType, entries }) {
  if (!entries || entries.length === 0) return null;
  const latest = entries[entries.length - 1];
  const rate = Number(latest.RateChange) || 0;
  const dir = latest.Direction || 'flat';
  const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '▶';
  const color = tileColor(rate, dir, marketType);
  const isAdmitted = marketType === 'Admitted';
  const narrative = marketNarrative(rate);

  return (
    <>
      <div className="FIG-tooltip-lob">{label}</div>
      <div className="FIG-tooltip-rate-row">
        <span className="FIG-tooltip-rate" style={{ color }}>
          {rate > 0 ? '+' : ''}{rate.toFixed(2)}%
        </span>
        <span className="FIG-tooltip-arrow" style={{ color }}>{arrow}</span>
      </div>
      <div className="FIG-tooltip-source">
        {isAdmitted
          ? `Source: Ivans Index — ${titleToFullDate(latest.Title)}`
          : `Source: CRC REDY — ${titleToQuarter(latest.Title)}`}
      </div>
      {isAdmitted ? (
        <div className="FIG-tooltip-history">
          {entries.slice(-6).reverse().map((e, i) => {
            const r = Number(e.RateChange) || 0;
            const d = e.Direction || 'flat';
            const a = d === 'up' ? '▲' : d === 'down' ? '▼' : '▶';
            const c = tileColor(r, d, 'Admitted');
            return (
              <div key={e.id || i} className="FIG-tooltip-history-row">
                <span className="FIG-tooltip-history-month">{titleToFullDate(e.Title).slice(0, 3)} {(e.Title || '').slice(2, 4)}</span>
                <span className="FIG-tooltip-history-rate" style={{ color: c }}>
                  {r > 0 ? '+' : ''}{r.toFixed(2)}%
                </span>
                <span className="FIG-tooltip-history-arrow" style={{ color: c }}>{a}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          {latest.DistReductionFlat != null && (() => {
            const segs = [
              { val: Number(latest.DistReductionFlat) || 0, cls: 'FIG-mkt-dist-seg--1', label: 'saw rate reductions or flat' },
              { val: Number(latest.Dist1to9) || 0, cls: 'FIG-mkt-dist-seg--2', label: 'saw 1–9% increases' },
              { val: Number(latest.Dist10to19) || 0, cls: 'FIG-mkt-dist-seg--3', label: 'saw 10–19% increases' },
              { val: Number(latest.Dist20Plus) || 0, cls: 'FIG-mkt-dist-seg--4', label: 'saw 20%+ increases' },
            ];
            const total = segs.reduce((s, x) => s + x.val, 0);
            return (
              <>
                <div className="FIG-tooltip-dist-lines">
                  {segs.filter(s => s.val > 0).map((s, i) => (
                    <div key={i} className="FIG-tooltip-dist-line">
                      {formatDistLine(s.val, `of renewals ${s.label}`)}
                    </div>
                  ))}
                </div>
                <div className="FIG-tooltip-dist-bar">
                  {segs.filter(s => s.val > 0).map((s, i) => (
                    <div key={i} className={'FIG-mkt-dist-seg ' + s.cls}
                      style={{ width: total > 0 ? `${(s.val / total) * 100}%` : '0%' }} />
                  ))}
                </div>
              </>
            );
          })()}
        </>
      )}
      <div className="FIG-tooltip-narrative">{narrative}</div>
    </>
  );
}

// Market context card inside gauge detail panel — with hover tooltip
function MarketContextCard({ market, onOpenMarket, openBrief, setOpenBrief }) {
  const [tip, setTip] = useState(false);
  const ref = useRef(null);
  const barRef = useRef(null);
  const briefId = market ? 'context-' + market.lob : null;
  const briefShow = briefId && openBrief?.id === briefId;
  const briefSegment = briefShow ? openBrief.segmentIndex : null;

  if (!market) return null;
  const latest = market.entries[market.entries.length - 1];
  const rate = Number(latest.RateChange) || 0;
  const dir = latest.Direction || 'flat';
  const isAdmitted = market.type === 'Admitted';
  const accentColor = isAdmitted ? '#e6a817' : '#cc3333';
  const rateColor = tileColor(rate, dir, market.type);
  const lobLabel = isAdmitted
    ? (LOB_LABELS_ADMITTED[market.lob] || market.lob)
    : (LOB_LABELS_SURPLUS[market.lob] || market.lob);
  const narrative = marketNarrative(rate);

  // Surplus distribution segments
  let distEl = null;
  if (!isAdmitted && latest.DistReductionFlat != null) {
    const segs = [
      { key: 0, val: Number(latest.DistReductionFlat) || 0, cls: 'FIG-mkt-dist-seg--1' },
      { key: 1, val: Number(latest.Dist1to9) || 0, cls: 'FIG-mkt-dist-seg--2' },
      { key: 2, val: Number(latest.Dist10to19) || 0, cls: 'FIG-mkt-dist-seg--3' },
      { key: 3, val: Number(latest.Dist20Plus) || 0, cls: 'FIG-mkt-dist-seg--4' },
    ].filter(s => s.val > 0);
    const total = segs.reduce((s, x) => s + x.val, 0);
    distEl = (
      <div className="FIG-detail-market-dist">
        <div ref={barRef}
          className={'FIG-detail-market-bar' + (setOpenBrief ? ' FIG-detail-market-bar--clickable' : '')}>
          {segs.map((s) => (
            <div key={s.key}
              className={'FIG-mkt-dist-seg ' + s.cls + (briefSegment === s.key ? ' FIG-mkt-dist-seg--selected' : '')}
              style={{ width: total > 0 ? `${(s.val / total) * 100}%` : '0%' }}
              onClick={setOpenBrief ? (e) => {
                e.stopPropagation();
                setOpenBrief(prev =>
                  prev?.id === briefId && prev?.segmentIndex === s.key
                    ? null
                    : { id: briefId, segmentIndex: s.key }
                );
              } : undefined} />
          ))}
        </div>
        <div className="FIG-detail-market-favorable">favorable: {Number(latest.DistReductionFlat) || 0}%</div>
      </div>
    );
  }

  // Admitted mini sparkline
  let sparkEl = null;
  if (isAdmitted && market.entries.length > 1) {
    const spark = market.entries.slice(-6).map(e => Number(e.RateChange) || 0);
    const min = Math.min(...spark), max = Math.max(...spark);
    const range = max - min || 1;
    const w = 120, h = 18;
    const pts = spark.map((v, i) => {
      const x = (i / (spark.length - 1)) * w;
      const y = h - ((v - min) / range) * (h - 2) - 1;
      return `${x},${y}`;
    }).join(' ');
    sparkEl = (
      <svg className="FIG-detail-market-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <polyline points={pts} fill="none" stroke={rateColor}
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <div className="FIG-detail-market-context">
      <div className="FIG-detail-market-header">MARKET CONDITIONS</div>
      <div ref={ref} className={'FIG-detail-market-card FIG-detail-market-card--clickable'
          + (onOpenMarket ? ' FIG-detail-market-card--linked' : '')}
        style={{ borderLeft: `3px solid ${accentColor}`, '--accent-color': accentColor }}
        onMouseEnter={() => !IS_TOUCH && setTip(true)}
        onMouseLeave={() => setTip(false)}
        onClick={onOpenMarket}>
        <div className="FIG-detail-market-row">
          <span className="FIG-detail-market-tag" style={{ color: accentColor }}>
            {isAdmitted ? 'ADMITTED' : 'SURPLUS'}
          </span>
          <span className="FIG-detail-market-lob">{lobLabel}</span>
          <span className="FIG-detail-market-rate" style={{ color: rateColor }}>
            {rate > 0 ? '+' : ''}{rate.toFixed(2)}%
          </span>
        </div>
        {distEl}
        {sparkEl}
        <div className="FIG-detail-market-narrative">{narrative}</div>
        {onOpenMarket && (
          <div className="FIG-detail-market-cta">VIEW FULL TREND →</div>
        )}
      </div>
      <MarketTooltip targetRef={ref} show={tip} marketType={market.type}>
        <div className="FIG-tooltip-context-large">
          <div className="FIG-tooltip-lob">{lobLabel}</div>
          <div className="FIG-tooltip-rate-row">
            <span className="FIG-tooltip-rate FIG-tooltip-rate--xl" style={{ color: rateColor }}>
              {rate > 0 ? '+' : ''}{rate.toFixed(2)}%
            </span>
            <span className="FIG-tooltip-arrow" style={{ color: rateColor }}>
              {dir === 'up' ? '▲' : dir === 'down' ? '▼' : '▶'}
            </span>
          </div>
          <div className="FIG-tooltip-source">
            {isAdmitted
              ? `Source: Ivans Index — ${titleToFullDate(latest.Title)}`
              : `Source: CRC REDY — ${titleToQuarter(latest.Title)}`}
          </div>
          {distEl && <div className="FIG-tooltip-context-bar">{distEl}</div>}
          {sparkEl && <div className="FIG-tooltip-context-spark">{sparkEl}</div>}
          <div className="FIG-tooltip-narrative">{narrative}</div>
          <div className="FIG-tooltip-cta">Click tile below for full 12-month trend</div>
        </div>
      </MarketTooltip>
      {!isAdmitted && latest.DistReductionFlat != null && (
        <DistributionBrief targetRef={barRef} show={briefShow} distData={{
          DistReductionFlat: latest.DistReductionFlat,
          Dist1to9: latest.Dist1to9,
          Dist10to19: latest.Dist10to19,
          Dist20Plus: latest.Dist20Plus,
        }} segmentIndex={briefSegment} />
      )}
    </div>
  );
}

function MarketTile({ lob, current, direction, history, animDelay = 0, onClick, marketEntries, pulseStagger = 0 }) {
  const label = LOB_LABELS_ADMITTED[lob] || LOB_LABELS[lob] || lob;
  const color = tileColor(current, direction, 'Admitted');
  const arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '▶';

  // Tooltip hover
  const [tip, setTip] = useState(false);
  const tileRef = useRef(null);

  // Illuminate animation
  const [lit, setLit] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLit(true), animDelay);
    return () => clearTimeout(t);
  }, [animDelay]);

  // Rate count-up
  const [displayRate, setDisplayRate] = useState(0);
  const countRef = useRef(null);
  useEffect(() => {
    if (!lit) return;
    const target = current;
    const duration = 1500;
    const startTime = performance.now();
    function step(now) {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const stepSize = Math.max(0.01, Math.abs(target) * 0.05 * (1 - eased));
      const raw = eased * target;
      const quantized = Math.round(raw / stepSize) * stepSize;
      setDisplayRate(progress >= 1 ? target : quantized);
      if (progress < 1) countRef.current = requestAnimationFrame(step);
    }
    countRef.current = requestAnimationFrame(step);
    return () => { if (countRef.current) cancelAnimationFrame(countRef.current); };
  }, [lit, current]);

  // Sparkline — 6 most recent data points
  const spark = (history || []).slice(-6);
  const sparkMin = Math.min(...spark.map(v => v));
  const sparkMax = Math.max(...spark.map(v => v));
  const sparkRange = sparkMax - sparkMin || 1;
  const sparkW = 80, sparkH = 18;
  const sparkPoints = spark.map((v, i) => {
    const x = spark.length > 1 ? (i / (spark.length - 1)) * sparkW : sparkW / 2;
    const y = sparkH - ((v - sparkMin) / sparkRange) * (sparkH - 2) - 1;
    return `${x},${y}`;
  }).join(' ');

  // Polyline total length — used to size the pulse-sweep dash pattern
  const sparkPathLen = (() => {
    if (spark.length < 2) return 0;
    let len = 0;
    const pts = spark.map((v, i) => [
      (i / (spark.length - 1)) * sparkW,
      sparkH - ((v - sparkMin) / sparkRange) * (sparkH - 2) - 1,
    ]);
    for (let i = 1; i < pts.length; i++) {
      const dx = pts[i][0] - pts[i - 1][0];
      const dy = pts[i][1] - pts[i - 1][1];
      len += Math.sqrt(dx * dx + dy * dy);
    }
    return len;
  })();

  // Sparkline draw-in animation
  const [sparkDrawn, setSparkDrawn] = useState(false);
  useEffect(() => {
    if (!lit) return;
    const t = setTimeout(() => setSparkDrawn(true), 600);
    return () => clearTimeout(t);
  }, [lit]);

  // Live-refresh digit-shuffle animation — visual only, no data refetch.
  // Each tile schedules its own shuffle independently.
  const latestRef = useRef({ current, spark });
  useEffect(() => { latestRef.current = { current, spark }; });
  const [shuffleRate, setShuffleRate] = useState(null);
  const [shuffleSparkA, setShuffleSparkA] = useState(null);
  const [shuffleSparkB, setShuffleSparkB] = useState(null);
  const shuffleTimerRef = useRef(null);
  const shuffleIntervalRef = useRef(null);
  useEffect(() => {
    if (!lit) return;
    function randAround(base, spread, decimals) {
      const v = Number(base) + (Math.random() - 0.5) * 2 * spread;
      return Number(v.toFixed(decimals));
    }
    function runShuffle() {
      const duration = 300 + Math.random() * 200;
      const tickMs = 55;
      const ticks = Math.max(1, Math.floor(duration / tickMs));
      let i = 0;
      shuffleIntervalRef.current = setInterval(() => {
        i += 1;
        if (i >= ticks) {
          clearInterval(shuffleIntervalRef.current);
          setShuffleRate(null);
          setShuffleSparkA(null);
          setShuffleSparkB(null);
          schedule(20_000 + Math.random() * 220_000);
          return;
        }
        const latest = latestRef.current;
        setShuffleRate(randAround(latest.current, 3, 2));
        if (latest.spark && latest.spark.length >= 2) {
          setShuffleSparkA(randAround(latest.spark[0], 2, 1).toFixed(1));
          setShuffleSparkB(randAround(latest.spark[latest.spark.length - 1], 2, 1).toFixed(1));
        }
      }, tickMs);
    }
    function schedule(delayMs) {
      shuffleTimerRef.current = setTimeout(runShuffle, delayMs);
    }
    schedule(2000 + Math.random() * 28_000);
    return () => {
      clearTimeout(shuffleTimerRef.current);
      clearInterval(shuffleIntervalRef.current);
    };
  }, [lit]);

  // Pulse sweep — plays once on mount (200ms after sparkDrawn, so the six
  // ADMITTED tiles cascade naturally via their existing animDelay stagger),
  // and replays on hover (debounced to 400ms so rapid re-hovers don't stack).
  const [pulseKey, setPulseKey] = useState(0);
  const pulseDebounceRef = useRef(0);
  function triggerPulse() {
    const now = Date.now();
    if (now - pulseDebounceRef.current < 400) return;
    pulseDebounceRef.current = now;
    setPulseKey(k => k + 1);
  }
  useEffect(() => {
    if (!sparkDrawn) return;
    const t = setTimeout(() => setPulseKey(k => k + 1), 200 + pulseStagger);
    return () => clearTimeout(t);
  }, [sparkDrawn, pulseStagger]);

  return (
    <>
    <div ref={tileRef}
      className={'FIG-mkt-tile' + (lit ? ' FIG-mkt-tile--lit' : '') + (onClick ? ' FIG-mkt-tile--clickable' : '')}
      style={{ '--tile-color': color }}
      onClick={onClick}
      onMouseEnter={() => {
        if (IS_TOUCH) return;
        setTip(true);
        triggerPulse();
      }}
      onMouseLeave={() => setTip(false)}>
      {/* Tier tag */}
      <div className="FIG-mkt-tier-tag FIG-mkt-tier-tag--admitted">ADMITTED</div>
      {/* LOB label */}
      <div className="FIG-mkt-label">{label}</div>
      {/* Rate readout */}
      <div className="FIG-mkt-rate" style={{ color }}>
        {lit
          ? (() => {
              const v = shuffleRate !== null ? shuffleRate : displayRate;
              return (v >= 0 ? '+' : '') + v.toFixed(2) + '%';
            })()
          : '—'}
      </div>
      {/* Direction arrow */}
      <div className="FIG-mkt-arrow" style={{ color }}>{lit ? arrow : ''}</div>
      {/* Sparkline with range labels */}
      <div className="FIG-mkt-spark-wrap">
        {spark.length > 1 && sparkDrawn && (
          <div className="FIG-mkt-spark-range" style={{ color }}>
            <span>{shuffleSparkA !== null ? shuffleSparkA : spark[0].toFixed(1)}</span>
            <span>{shuffleSparkB !== null ? shuffleSparkB : spark[spark.length - 1].toFixed(1)}</span>
          </div>
        )}
        <svg className={'FIG-mkt-spark' + (sparkDrawn ? ' FIG-mkt-spark--drawn' : '')}
          viewBox={`0 0 ${sparkW} ${sparkH}`} preserveAspectRatio="none">
          {spark.length > 1 && (
            <polyline points={sparkPoints} fill="none" stroke={color}
              strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          )}
          {spark.length > 1 && pulseKey > 0 && sparkPathLen > 0 && (
            <polyline
              key={`pulse-${pulseKey}`}
              className="FIG-mkt-spark-pulse"
              points={sparkPoints}
              fill="none"
              stroke={color}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                '--pulse-length': `${sparkPathLen.toFixed(2)}px`,
                color,
              }}
            />
          )}
        </svg>
      </div>
    </div>
    <MarketTooltip targetRef={tileRef} show={tip} marketType="Admitted">
      <TileTooltipContent lob={lob} label={label} marketType="Admitted" entries={marketEntries} />
    </MarketTooltip>
    </>
  );
}

/* ========================================================================
   Surplus Market Tile — distribution bar instead of sparkline
   ======================================================================== */

function SurplusMarketTile({ lob, current, direction, distData, animDelay = 0, onClick, marketEntries, openBrief, setOpenBrief }) {
  const label = LOB_LABELS_SURPLUS[lob] || lob;
  const color = tileColor(current, direction, 'Surplus');
  const arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '▶';

  // Tooltip hover
  const [tip, setTip] = useState(false);
  const tileRef = useRef(null);
  const barRef = useRef(null);
  const briefId = 'tile-' + lob;
  const briefShow = openBrief?.id === briefId;
  const briefSegment = briefShow ? openBrief.segmentIndex : null;

  const [lit, setLit] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setLit(true), animDelay);
    return () => clearTimeout(t);
  }, [animDelay]);

  // Rate count-up
  const [displayRate, setDisplayRate] = useState(0);
  const countRef = useRef(null);
  useEffect(() => {
    if (!lit) return;
    const target = current;
    const duration = 1500;
    const startTime = performance.now();
    function step(now) {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const stepSize = Math.max(0.01, Math.abs(target) * 0.05 * (1 - eased));
      const raw = eased * target;
      const quantized = Math.round(raw / stepSize) * stepSize;
      setDisplayRate(progress >= 1 ? target : quantized);
      if (progress < 1) countRef.current = requestAnimationFrame(step);
    }
    countRef.current = requestAnimationFrame(step);
    return () => { if (countRef.current) cancelAnimationFrame(countRef.current); };
  }, [lit, current]);

  // Distribution bar — normalize segments to 100% if data exists. Preserve original key (0-3).
  const d = distData || {};
  const segs = [
    { key: 0, val: Number(d.DistReductionFlat) || 0, cls: 'FIG-mkt-dist-seg--1' },
    { key: 1, val: Number(d.Dist1to9) || 0, cls: 'FIG-mkt-dist-seg--2' },
    { key: 2, val: Number(d.Dist10to19) || 0, cls: 'FIG-mkt-dist-seg--3' },
    { key: 3, val: Number(d.Dist20Plus) || 0, cls: 'FIG-mkt-dist-seg--4' },
  ].filter(s => s.val > 0);
  const totalDist = segs.reduce((sum, s) => sum + s.val, 0);
  const favorable = Number(d.DistReductionFlat) || 0;

  const [barDrawn, setBarDrawn] = useState(false);
  useEffect(() => {
    if (!lit) return;
    const t = setTimeout(() => setBarDrawn(true), 600);
    return () => clearTimeout(t);
  }, [lit]);

  // Live-refresh digit-shuffle animation — visual only, no data refetch.
  const latestRef = useRef({ current });
  useEffect(() => { latestRef.current = { current }; });
  const [shuffleRate, setShuffleRate] = useState(null);
  const shuffleTimerRef = useRef(null);
  const shuffleIntervalRef = useRef(null);
  useEffect(() => {
    if (!lit) return;
    function randAround(base, spread, decimals) {
      const v = Number(base) + (Math.random() - 0.5) * 2 * spread;
      return Number(v.toFixed(decimals));
    }
    function runShuffle() {
      const duration = 300 + Math.random() * 200;
      const tickMs = 55;
      const ticks = Math.max(1, Math.floor(duration / tickMs));
      let i = 0;
      shuffleIntervalRef.current = setInterval(() => {
        i += 1;
        if (i >= ticks) {
          clearInterval(shuffleIntervalRef.current);
          setShuffleRate(null);
          schedule(20_000 + Math.random() * 220_000);
          return;
        }
        setShuffleRate(randAround(latestRef.current.current, 3, 2));
      }, tickMs);
    }
    function schedule(delayMs) {
      shuffleTimerRef.current = setTimeout(runShuffle, delayMs);
    }
    schedule(2000 + Math.random() * 28_000);
    return () => {
      clearTimeout(shuffleTimerRef.current);
      clearInterval(shuffleIntervalRef.current);
    };
  }, [lit]);

  return (
    <>
    <div ref={tileRef}
      className={'FIG-mkt-tile FIG-mkt-tile--surplus' + (lit ? ' FIG-mkt-tile--lit' : '') + (onClick ? ' FIG-mkt-tile--clickable' : '')}
      style={{ '--tile-color': color }}
      onClick={onClick}
      onMouseEnter={() => !IS_TOUCH && setTip(true)}
      onMouseLeave={() => setTip(false)}>
      <div className="FIG-mkt-tier-tag FIG-mkt-tier-tag--surplus">SURPLUS</div>
      <div className="FIG-mkt-label">{label}</div>
      <div className="FIG-mkt-rate" style={{ color }}>
        {lit
          ? (() => {
              const v = shuffleRate !== null ? shuffleRate : displayRate;
              return (v >= 0 ? '+' : '') + v.toFixed(2) + '%';
            })()
          : '—'}
      </div>
      <div className="FIG-mkt-arrow" style={{ color }}>{lit ? arrow : ''}</div>
      <div className="FIG-mkt-dist-wrap">
        <div ref={barRef}
          className={'FIG-mkt-dist-bar' + (barDrawn ? ' FIG-mkt-dist-bar--drawn' : '') + (distData && setOpenBrief ? ' FIG-mkt-dist-bar--clickable' : '')}>
          {segs.map((s) => (
            <div key={s.key}
              className={'FIG-mkt-dist-seg ' + s.cls + (briefSegment === s.key ? ' FIG-mkt-dist-seg--selected' : '')}
              style={{ width: barDrawn && totalDist > 0 ? `${(s.val / totalDist) * 100}%` : '0%' }}
              onClick={distData && setOpenBrief ? (e) => {
                e.stopPropagation();
                setOpenBrief(prev =>
                  prev?.id === briefId && prev?.segmentIndex === s.key
                    ? null
                    : { id: briefId, segmentIndex: s.key }
                );
              } : undefined} />
          ))}
        </div>
        {totalDist > 0 && barDrawn && (
          <div className="FIG-mkt-dist-label">favorable: {favorable}%</div>
        )}
      </div>
    </div>
    <MarketTooltip targetRef={tileRef} show={tip} marketType="Surplus">
      <TileTooltipContent lob={lob} label={label} marketType="Surplus" entries={marketEntries} />
    </MarketTooltip>
    <DistributionBrief targetRef={barRef} show={briefShow} distData={distData} segmentIndex={briefSegment} />
    </>
  );
}

/* ========================================================================
   Main Component
   ======================================================================== */

function FIGRenewalPipeline({ getToken, onSelectInsured, selectedClientId: initialClientId, onSelectedClientChange }) {
  const [policies, setPolicies] = useState([]);
  const [insureds, setInsureds] = useState([]);
  const [marketData, setMarketData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const token = await getToken();
      const [pols, ins, mkt] = await Promise.all([
        getItems(token, FIG_LISTS.POLICIES),
        getItems(token, FIG_LISTS.INSUREDS),
        getMarketConditions(token).catch(() => []),
      ]);
      setPolicies(pols);
      setInsureds(ins);
      setMarketData(mkt);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // FIG logo badge — fetch from Vault using the same asset path invoice PDFs use
  const [logoUrl, setLogoUrl] = useState(null);
  useEffect(() => {
    let revokeUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const settings = await getAgencySettings(getToken);
        if (cancelled || !settings?.BrandLogoHorizontalPath) return;
        const token = await getToken();
        const blob = await getFromFIGVault(token, settings.BrandLogoHorizontalPath);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        revokeUrl = url;
        setLogoUrl(url);
      } catch (err) {
        console.warn('[FIG Cockpit] Logo load skipped:', err.message);
      }
    })();
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [getToken]);

  const insuredMap = useMemo(() => {
    const map = {};
    insureds.forEach(i => { map[String(i.id)] = i; });
    return map;
  }, [insureds]);

  // All active/bound policies with days calculated
  const allActive = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return policies
      .filter(p => { const st = (p.Status || '').toLowerCase(); return st === 'active' || st === 'bound'; })
      .map(p => {
        const exp = parseLocalDate(p.ExpirationDate);
        const days = exp ? Math.ceil((exp - now) / (1000 * 60 * 60 * 24)) : null;
        return { ...p, daysUntilExp: days };
      })
      .filter(p => p.daysUntilExp !== null && p.daysUntilExp >= 0)
      .sort((a, b) => a.daysUntilExp - b.daysUntilExp);
  }, [policies]);

  // Buckets
  const { red, yellow, green, grey } = useMemo(() => {
    const buckets = { red: [], yellow: [], green: [], grey: [] };
    allActive.forEach(p => {
      if (p.daysUntilExp <= 30) buckets.red.push(p);
      else if (p.daysUntilExp <= 90) buckets.yellow.push(p);
      else if (p.daysUntilExp <= 180) buckets.green.push(p);
      else buckets.grey.push(p);
    });
    return buckets;
  }, [allActive]);

  // Market conditions — group by LOB, sorted chronologically
  const { marketAdmitted, marketSurplus } = useMemo(() => {
    const admitted = {};
    const surplus = {};
    for (const row of marketData) {
      const target = row.MarketType === 'Surplus' ? surplus : admitted;
      if (!target[row.LOB]) target[row.LOB] = [];
      target[row.LOB].push(row);
    }
    for (const group of [admitted, surplus]) {
      for (const lob of Object.keys(group)) {
        group[lob].sort((a, b) => (a.Title || '').localeCompare(b.Title || ''));
      }
    }
    return { marketAdmitted: admitted, marketSurplus: surplus };
  }, [marketData]);

  // Latest published date across all market data
  const latestPublished = useMemo(() => {
    let latest = '';
    for (const row of marketData) {
      if (row.PublishedDate && row.PublishedDate > latest) latest = row.PublishedDate;
    }
    return latest;
  }, [marketData]);

  // Client carousel hooks — must be before early returns
  const clientCarousel = useMemo(() => {
    const clientMap = {};
    allActive.forEach(p => {
      const key = String(p.InsuredId);
      if (!clientMap[key]) {
        const ins = insuredMap[key];
        clientMap[key] = {
          insuredId: key,
          name: ins ? (ins.CompanyName || ins.Title || 'Unknown') : 'Unknown',
          totalPrem: 0,
          soonest: null,
        };
      }
      clientMap[key].totalPrem += Number(p.Premium) || 0;
      if (!clientMap[key].soonest || p.daysUntilExp < clientMap[key].soonest.daysUntilExp) {
        clientMap[key].soonest = p;
      }
    });
    return Object.values(clientMap).sort((a, b) => b.totalPrem - a.totalPrem);
  }, [allActive, insuredMap]);

  // Client indicator strip state
  const [stripOffset, setStripOffset] = useState(0);
  const [stripDragX, setStripDragX] = useState(0);
  const [stripDragging, setStripDragging] = useState(false);
  const [selectedClientId, setSelectedClientIdLocal] = useState(initialClientId || null);

  // Sync client selection back to parent so it persists across unmount/remount
  function setSelectedClientId(val) {
    const resolved = typeof val === 'function' ? val(selectedClientId) : val;
    setSelectedClientIdLocal(resolved);
    if (onSelectedClientChange) onSelectedClientChange(resolved);
  }
  const stripRef = useRef({ startX: 0, dragging: false });
  const stripViewportRef = useRef(null);

  // CLIENTS-label dropdown — alphabetical list of all clients, scrollable
  const [clientsOpen, setClientsOpen] = useState(false);
  const clientsBtnRef = useRef(null);
  const clientsDropdownRef = useRef(null);
  const sortedClients = useMemo(
    () => [...clientCarousel].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [clientCarousel]
  );
  useEffect(() => {
    if (!clientsOpen) return;
    function onDocMouseDown(e) {
      if (
        clientsDropdownRef.current && !clientsDropdownRef.current.contains(e.target) &&
        clientsBtnRef.current && !clientsBtnRef.current.contains(e.target)
      ) {
        setClientsOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [clientsOpen]);

  const stripMaxOffset = useMemo(() => Math.max(0, clientCarousel.length - 4), [clientCarousel.length]);

  // Drag-to-scroll with 5px click-vs-drag threshold (works on grip, viewport, and tiles)
  const DRAG_THRESHOLD = 5;

  const handleStripDragStart = useCallback((e) => {
    if (clientCarousel.length <= 4) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    stripRef.current = { startX: x, dragging: false, engaged: false, startOffset: stripOffset };

    function onMove(ev) {
      const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const dx = cx - stripRef.current.startX;
      // Engage drag mode once threshold exceeded
      if (!stripRef.current.engaged && Math.abs(dx) >= DRAG_THRESHOLD) {
        stripRef.current.engaged = true;
        stripRef.current.dragging = true;
        setStripDragging(true);
      }
      if (stripRef.current.engaged) {
        if (ev.cancelable) ev.preventDefault(); // prevent text selection / scroll during drag
        setStripDragX(dx);
      }
    }

    function onEnd(ev) {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      const wasDrag = stripRef.current.engaged;
      stripRef.current.dragging = false;
      stripRef.current.engaged = false;
      setStripDragging(false);
      if (wasDrag) {
        const cx = ev.changedTouches ? ev.changedTouches[0].clientX : ev.clientX;
        const dx = cx - stripRef.current.startX;
        const vp = stripViewportRef.current;
        const cardWidth = vp ? (vp.offsetWidth - 15) / 4 : 150;
        const threshold = cardWidth * 0.3;
        const cards = Math.round(Math.abs(dx) / cardWidth);
        if (Math.abs(dx) >= threshold && cards > 0) {
          const dir = dx < 0 ? cards : -cards;
          setStripOffset(prev => Math.max(0, Math.min(stripMaxOffset, prev + dir)));
        }
        setStripDragX(0);
        // Suppress the click that follows mouseup on tiles
        const suppress = (ce) => { ce.stopPropagation(); ce.preventDefault(); };
        document.addEventListener('click', suppress, { capture: true, once: true });
        setTimeout(() => document.removeEventListener('click', suppress, { capture: true }), 0);
      } else {
        setStripDragX(0);
      }
    }

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  }, [clientCarousel.length, stripOffset, stripMaxOffset]);

  // No auto-select — null = HOME (three agency-level gauges)

  // Policies for the currently selected client
  const selectedPolicies = useMemo(() => {
    if (!selectedClientId) return [];
    return allActive.filter(p => String(p.InsuredId) === selectedClientId);
  }, [allActive, selectedClientId]);

  // Quoted policies for the current client — separate stream so downstream
  // Active-only consumers (beacon, tile strip) stay unaffected.
  const selectedQuoted = useMemo(() => {
    if (!selectedClientId) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return policies
      .filter(p => String(p.InsuredId) === selectedClientId
               && (p.Status || '').toLowerCase() === 'quoted')
      .map(p => {
        const exp = parseLocalDate(p.ExpirationDate);
        const days = exp ? Math.ceil((exp - today) / 86_400_000) : null;
        return { ...p, daysUntilExp: days };
      })
      .sort((a, b) => {
        const ea = parseLocalDate(a.EffectiveDate)?.getTime() ?? 0;
        const eb = parseLocalDate(b.EffectiveDate)?.getTime() ?? 0;
        return eb - ea;
      });
  }, [policies, selectedClientId]);

  // Gauge-grid mode switch — ACTIVE (default) vs QUOTED. The invoice flow
  // follows this mode; beacon and tile strip remain Active-only.
  const [gaugeMode, setGaugeMode] = useState('active');
  useEffect(() => { setGaugeMode('active'); }, [selectedClientId]);
  const currentPolicies = gaugeMode === 'quoted' ? selectedQuoted : selectedPolicies;

  // Emergency beacon — soonest policy across ALL clients
  const beaconPolicy = useMemo(() => allActive[0] || null, [allActive]);

  // HOME aggregates — shown when no client is selected
  const totalBookPremium = useMemo(
    () => allActive.reduce((s, p) => s + (Number(p.Premium) || 0), 0),
    [allActive]
  );
  const agencyRevenue = totalBookPremium * 0.14;
  const activeCount = allActive.length;
  const revMax = Math.max(180_000, Math.ceil((agencyRevenue * 1.3) / 30_000) * 30_000);
  const countMax = Math.max(10, Math.ceil((activeCount * 1.4) / 5) * 5);

  // Claude prompt builder state
  const [claudePromptOpen, setClaudePromptOpen] = useState(false);
  const [claudeCopied, setClaudeCopied] = useState(false);

  // JBB modal state
  const [jbbOpen, setJbbOpen] = useState(false);
  const [jbbPolicyContext, setJbbPolicyContext] = useState(null);

  // Invoice flow state
  const [invoiceStep, setInvoiceStep] = useState(null); // null | 'selector' | 'builder'
  const [invoiceSelectedPolicies, setInvoiceSelectedPolicies] = useState([]);

  // ClientActionPanel — right-side cockpit navigation, visible when a client
  // is selected. Nameplate auto-detects overflow to decide between static-ellipsis
  // and scrolling marquee with ease-in/cruise/ease-out.
  const namePlateMaskRef = useRef(null);
  const namePlateTrackRef = useRef(null);
  const [nameOverflows, setNameOverflows] = useState(false);
  useLayoutEffect(() => {
    if (!selectedClientId || !namePlateMaskRef.current || !namePlateTrackRef.current) {
      setNameOverflows(false);
      return;
    }
    const id = requestAnimationFrame(() => {
      const maskW = namePlateMaskRef.current?.clientWidth || 0;
      const firstText = namePlateTrackRef.current?.firstElementChild;
      const textW = firstText?.offsetWidth || 0;
      setNameOverflows(textW > maskW - 4);
    });
    return () => cancelAnimationFrame(id);
  }, [selectedClientId]);

  // Status-bar nameplate overflow detection — mirrors the action-panel pattern.
  const statusNameMaskRef = useRef(null);
  const statusNameTrackRef = useRef(null);
  const [statusNameOverflows, setStatusNameOverflows] = useState(false);
  useLayoutEffect(() => {
    if (!selectedClientId || !statusNameMaskRef.current || !statusNameTrackRef.current) {
      setStatusNameOverflows(false);
      return;
    }
    const id = requestAnimationFrame(() => {
      const maskW = statusNameMaskRef.current?.clientWidth || 0;
      const firstText = statusNameTrackRef.current?.firstElementChild;
      const textW = firstText?.offsetWidth || 0;
      setStatusNameOverflows(textW > maskW - 4);
    });
    return () => cancelAnimationFrame(id);
  }, [selectedClientId]);

  // Collapsible-drawer state for the ClientActionPanel.
  // open = panelPinned || panelHovered. panelHovered is debounced on leave.
  const [panelPinned, setPanelPinned] = useState(false);
  const [panelHovered, setPanelHovered] = useState(false);
  const panelHoverTimerRef = useRef(null);
  const handleRef = useRef(null);
  const panelRef = useRef(null);

  const onHandleEnter = useCallback(() => {
    clearTimeout(panelHoverTimerRef.current);
    setPanelHovered(true);
  }, []);
  const onHandleLeave = useCallback(() => {
    if (panelPinned) return;
    clearTimeout(panelHoverTimerRef.current);
    panelHoverTimerRef.current = setTimeout(() => setPanelHovered(false), 400);
  }, [panelPinned]);
  const onHandleClick = useCallback(() => {
    setPanelPinned(prev => {
      const next = !prev;
      if (next) { clearTimeout(panelHoverTimerRef.current); setPanelHovered(true); }
      return next;
    });
  }, []);
  const onPanelEnter = useCallback(() => {
    clearTimeout(panelHoverTimerRef.current);
    setPanelHovered(true);
  }, []);
  const onPanelLeave = useCallback(() => {
    if (panelPinned) return;
    clearTimeout(panelHoverTimerRef.current);
    panelHoverTimerRef.current = setTimeout(() => setPanelHovered(false), 400);
  }, [panelPinned]);

  // Click-outside-to-unpin. Preserves state when clicking client tiles.
  useEffect(() => {
    if (!panelPinned) return;
    function onDocMouseDown(e) {
      const t = e.target;
      if (
        (panelRef.current && panelRef.current.contains(t)) ||
        (handleRef.current && handleRef.current.contains(t)) ||
        (t.closest && t.closest('.FIG-client-card'))
      ) {
        return;
      }
      setPanelPinned(false);
      setPanelHovered(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [panelPinned]);

  // Reset drawer state when leaving to HOME; cleanup timer on unmount.
  useEffect(() => {
    if (!selectedClientId) {
      setPanelPinned(false);
      setPanelHovered(false);
      clearTimeout(panelHoverTimerRef.current);
    }
  }, [selectedClientId]);
  useEffect(() => () => clearTimeout(panelHoverTimerRef.current), []);

  // Detail view state
  const [detailPolicy, setDetailPolicy] = useState(null);
  const [detailExiting, setDetailExiting] = useState(false);
  const [returnHighlight, setReturnHighlight] = useState(null);

  // Market detail panel state
  const [marketDetail, setMarketDetail] = useState(null); // { lob, marketType }
  const [marketDetailExiting, setMarketDetailExiting] = useState(false);

  function openMarketDetail(lob, marketType) {
    if (detailPolicy) { setDetailPolicy(null); setDetailExiting(false); }
    setMarketDetail({ lob, marketType });
    setMarketDetailExiting(false);
  }

  function closeMarketDetail() {
    if (!marketDetail) return;
    setMarketDetailExiting(true);
    setTimeout(() => { setMarketDetail(null); setMarketDetailExiting(false); }, 300);
  }

  // Theatrical power-cycle state (incremented to force re-mount & replay animations)
  const [powerCycle, setPowerCycle] = useState(0);
  const [trimPulled, setTrimPulled] = useState(false);

  // Market readiness popover
  const [marketPopoverId, setMarketPopoverId] = useState(null);

  // Distribution brief popup — { id, segmentIndex } or null. Only one open at a time.
  const [openBrief, setOpenBrief] = useState(null);
  useEffect(() => {
    if (!openBrief) return;
    const handler = () => setOpenBrief(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openBrief]);

  function openDetail(policy) {
    if (marketDetail) { setMarketDetail(null); setMarketDetailExiting(false); }
    setDetailPolicy(policy);
    setDetailExiting(false);
    setReturnHighlight(null);
  }

  function closeDetail() {
    if (!detailPolicy) return;
    const returningId = detailPolicy.id;
    setDetailExiting(true);
    setTimeout(() => {
      setDetailPolicy(null);
      setDetailExiting(false);
      setReturnHighlight(returningId);
      setTimeout(() => setReturnHighlight(null), 1200);
    }, 300);
  }

  function handleClick(policy) {
    const ins = insuredMap[String(policy.InsuredId)];
    if (ins && onSelectInsured) onSelectInsured(ins);
  }

  // Beacon click — switch to that client and open detail
  function handleBeaconClick() {
    if (!beaconPolicy) return;
    const clientId = String(beaconPolicy.InsuredId);
    if (clientId !== selectedClientId) setSelectedClientId(clientId);
    openDetail(beaconPolicy);
  }

  function fmtDate(d) {
    if (!d) return '—';
    const dt = parseLocalDate(d);
    if (!dt) return d;
    return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
  }

  function handleTrimReset() {
    if (trimPulled) return;
    setTrimPulled(true);
    // Close detail if open
    if (detailPolicy) {
      setDetailPolicy(null);
      setDetailExiting(false);
    }
    setReturnHighlight(null);
    // Reset to HOME (no client selected)
    setSelectedClientId(null);
    setStripOffset(0);
    // Trigger full re-mount after lever animation
    setTimeout(() => {
      setPowerCycle(c => c + 1);
      setTrimPulled(false);
    }, 350);
  }

  if (loading) return <p className="FIG-loading">Loading renewal pipeline...</p>;
  if (error) return <p className="FIG-error">{error}</p>;

  const within180 = red.length + yellow.length + green.length;
  const total = allActive.length;

  // Month tick labels — 12 months from effective to expiration
  function buildMonthTicks(policy) {
    if (!policy || !policy.EffectiveDate) return null;
    const eff = parseLocalDate(policy.EffectiveDate);
    if (!eff) return null;
    const startMonth = eff.getMonth() + 1;
    return Array.from({ length: 12 }, (_, i) => MONTH_NAMES[(startMonth + i) % 12]);
  }

  function daysElapsed(policy) {
    if (!policy || !policy.EffectiveDate || !policy.ExpirationDate) return 0;
    const eff = parseLocalDate(policy.EffectiveDate);
    const exp = parseLocalDate(policy.ExpirationDate);
    if (!eff || !exp) return 0;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const tt = Math.max(1, Math.ceil((exp - eff) / (1000 * 60 * 60 * 24)));
    const elapsed = Math.ceil((now - eff) / (1000 * 60 * 60 * 24));
    return Math.max(0, Math.min(tt, elapsed));
  }

  function totalTerm(policy) {
    if (!policy || !policy.EffectiveDate || !policy.ExpirationDate) return 365;
    const eff = parseLocalDate(policy.EffectiveDate);
    const exp = parseLocalDate(policy.ExpirationDate);
    if (!eff || !exp) return 365;
    return Math.max(1, Math.ceil((exp - eff) / (1000 * 60 * 60 * 24)));
  }

  function gaugeInterior(policy) {
    if (!policy) return { month: '—', premium: null, color: '#44cc44', pulse: undefined };
    const expDt = parseLocalDate(policy.ExpirationDate);
    const m = expDt ? MONTH_FULL[expDt.getMonth()] : '—';
    const prem = policy.Premium ? formatCurrencyFull(policy.Premium) : null;
    const d = policy.daysUntilExp ?? 999;
    const color = d <= 30 ? '#ff4444' : d <= 90 ? '#e6a817' : '#44cc44';
    const pulse = d <= 30 ? 'FIG-led--red-pulse' : d <= 90 ? 'FIG-led--yellow-pulse' : undefined;
    return { month: m, premium: prem, color, pulse };
  }

  // Gauge grid sizing — determine size class and columns based on policy count
  const n = currentPolicies.length;
  const gaugeSize = n <= 4 ? 'lg' : n <= 9 ? 'md' : n <= 16 ? 'sm' : 'compact';
  const gaugeCols = n <= 2 ? n : n <= 4 ? 4 : n <= 9 ? 3 : n <= 16 ? 4 : 5;

  // Emergency beacon data
  const beaconDays = beaconPolicy?.daysUntilExp ?? 999;
  const beaconColor = beaconDays <= 30 ? '#ef4444' : beaconDays <= 90 ? '#f59e0b' : '#22c55e';
  const beaconClient = beaconPolicy
    ? (insuredMap[String(beaconPolicy.InsuredId)]?.CompanyName || 'UNK')
    : '—';
  const beaconClientAbbr = beaconClient.length > 10
    ? beaconClient.split(/[\s,]+/)[0].toUpperCase()
    : beaconClient.toUpperCase();
  const beaconLOB = beaconPolicy
    ? (beaconPolicy.LOB || 'POL').replace(/\s+/g, '').substring(0, 6).toUpperCase()
    : '—';

  return (
    <div className="FIG-renewal-pipeline">
      {/* ---- INSTRUMENT CLUSTER ---- */}
      <div className="FIG-cessna-dashboard">
        {/* Glareshield — padded top rail */}
        <div className="FIG-cessna-glareshield">
          {/* N-number registration plate */}
          <div className="FIG-cessna-nplate">FIG-001</div>
        </div>

        <div className="FIG-cessna-panel">
          {/* Panel screws — scattered organically */}
          <svg className="FIG-panel-screw" style={{top:'10px',left:'12px'}} viewBox="0 0 10 10"><PhillipsScrew sx={5} sy={5} sr={4} /></svg>
          <svg className="FIG-panel-screw" style={{top:'10px',left:'52px'}} viewBox="0 0 10 10"><PhillipsScrew sx={5} sy={5} sr={4} /></svg>
          <svg className="FIG-panel-screw" style={{bottom:'14px',left:'10px'}} viewBox="0 0 10 10"><PhillipsScrew sx={5} sy={5} sr={4} /></svg>
          <svg className="FIG-panel-screw" style={{bottom:'14px',right:'10px'}} viewBox="0 0 10 10"><PhillipsScrew sx={5} sy={5} sr={4} /></svg>
          <svg className="FIG-panel-screw" style={{top:'50%',left:'8px'}} viewBox="0 0 10 10"><PhillipsScrew sx={5} sy={5} sr={4} /></svg>
          <svg className="FIG-panel-screw" style={{top:'10px',right:'30px'}} viewBox="0 0 10 10"><PhillipsScrew sx={5} sy={5} sr={4} /></svg>

          {/* Upper-left FIG badge — instrument-panel style, HOME only */}
          {!selectedClientId && logoUrl && (
            <img className="FIG-cessna-badge" src={logoUrl} alt="FIG" draggable="false" />
          )}

          {/* ---- ANIMATED PANEL CONTENT (re-mounts on power cycle) ---- */}
          <div key={powerCycle}>

          {/* ---- CLIENT INDICATOR STRIP ---- */}
          <div className="FIG-client-strip FIG-entrance-strip">
            <div className="FIG-client-strip-label">
              <button
                type="button"
                className={'FIG-client-strip-home' + (!selectedClientId ? ' FIG-client-strip-home--active' : '')}
                onClick={() => setSelectedClientId(null)}
                title="Return to HOME — agency gauges"
              >HOME</button>
              <span className="FIG-client-strip-sep" aria-hidden="true">·</span>
              <span className="FIG-client-strip-clients-wrap">
                <button
                  ref={clientsBtnRef}
                  type="button"
                  className={'FIG-client-strip-clients-btn' + (clientsOpen ? ' FIG-client-strip-clients-btn--active' : '')}
                  onClick={() => setClientsOpen(o => !o)}
                  aria-haspopup="listbox"
                  aria-expanded={clientsOpen}
                >CLIENTS</button>
                {clientsOpen && (
                  <div
                    ref={clientsDropdownRef}
                    className="FIG-client-dropdown"
                    role="listbox"
                  >
                    {sortedClients.length === 0 && (
                      <div className="FIG-client-dropdown-empty">NO CLIENTS</div>
                    )}
                    {sortedClients.map(c => {
                      const isSelected = selectedClientId === c.insuredId;
                      return (
                        <button
                          key={c.insuredId}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          className={'FIG-client-dropdown-row' + (isSelected ? ' FIG-client-dropdown-row--selected' : '')}
                          onClick={() => {
                            setSelectedClientId(c.insuredId);
                            setClientsOpen(false);
                          }}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </span>
            </div>
            <div className="FIG-client-strip-body">
              {/* Magneto grip handle */}
              <div
                className={'FIG-client-grip' + (stripDragging ? ' FIG-client-grip--active' : '')}
                onMouseDown={handleStripDragStart}
                onTouchStart={handleStripDragStart}
                style={{ cursor: clientCarousel.length > 4 ? (stripDragging ? 'grabbing' : 'grab') : 'default' }}
              >
                {[0,1,2,3,4,5,6].map(i => (
                  <div key={i} className="FIG-client-grip-line" />
                ))}
                {clientCarousel.length > 4 && (
                  <div className={'FIG-client-grip-arrows' + (stripDragging ? ' FIG-client-grip-arrows--active' : '')}>◀▶</div>
                )}
              </div>

              {/* Client card viewport */}
              <div className="FIG-client-viewport" ref={stripViewportRef}
                onMouseDown={handleStripDragStart}
                onTouchStart={handleStripDragStart}>
                {/* Left fade */}
                {stripOffset > 0 && <div className="FIG-client-fade FIG-client-fade--left" />}
                {/* Right fade */}
                {stripOffset < stripMaxOffset && clientCarousel.length > 4 && (
                  <div className="FIG-client-fade FIG-client-fade--right" />
                )}

                <div className="FIG-client-track" style={{
                  transform: `translateX(calc(${-stripOffset * 25}% + ${stripDragX}px - ${stripOffset * 5}px))`,
                  transition: stripDragging ? 'none' : 'transform 0.35s ease-out',
                }}>
                  {(() => {
                    const totalSlots = Math.max(4, clientCarousel.length);
                    const cards = [];
                    for (let i = 0; i < totalSlots; i++) {
                      const client = clientCarousel[i];
                      if (client) {
                        const days = client.soonest?.daysUntilExp ?? 999;
                        const status = days <= 30 ? 'red' : days <= 90 ? 'amber' : 'green';
                        const statusColor = status === 'red' ? '#ef4444' : status === 'amber' ? '#f59e0b' : '#22c55e';
                        const isSelected = selectedClientId === client.insuredId;
                        const policyCount = allActive.filter(p => String(p.InsuredId) === client.insuredId).length;
                        cards.push(
                          <div key={client.insuredId}
                            className={'FIG-client-card' + (isSelected ? ' FIG-client-card--selected' : '')}
                            onClick={() => {
                              if (detailPolicy) {
                                setDetailPolicy(null); setDetailExiting(false);
                                if (selectedClientId === client.insuredId) return; // same client — just close detail
                              }
                              setSelectedClientId(prev => prev === client.insuredId ? null : client.insuredId);
                            }}
                          >
                            <div className={'FIG-client-card-bar FIG-client-card-bar--' + status} style={{ background: statusColor }} />
                            <div className={'FIG-client-led FIG-client-led--' + status} style={{ background: statusColor }} />
                            <div className="FIG-client-card-name">{client.name}</div>
                            <div className="FIG-client-card-prem">{formatCurrency(client.totalPrem)}</div>
                            <div className="FIG-client-card-badges">
                              <span className="FIG-client-badge FIG-client-badge--count">{policyCount}</span>
                              {status === 'green' && <span className="FIG-client-badge FIG-client-badge--clear">ALL CLEAR</span>}
                              {status === 'red' && <span className="FIG-client-badge FIG-client-badge--exp">{days}d</span>}
                              {status === 'amber' && <span className="FIG-client-badge FIG-client-badge--warn">{days}d</span>}
                            </div>
                          </div>
                        );
                      } else {
                        // Ghost card
                        cards.push(
                          <div key={'ghost-' + i} className="FIG-client-card FIG-client-card--ghost">
                            <div className="FIG-client-card-bar" style={{ background: '#333' }} />
                            <div className="FIG-client-card-ghost-text">—— open ——</div>
                          </div>
                        );
                      }
                    }
                    return cards;
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* ---- EMERGENCY BEACON ---- */}
          {beaconPolicy && (
            <div className={'FIG-beacon FIG-entrance-beacon' + (beaconDays <= 30 ? ' FIG-beacon--red' : '')}
              onClick={handleBeaconClick} title="Jump to soonest expiring policy">
              <div className={'FIG-beacon-led' + (beaconDays <= 30 ? ' FIG-beacon-led--pulse' : '')}
                style={{ background: beaconColor, boxShadow: `0 0 6px ${beaconColor}` }} />
              <div className="FIG-beacon-text" style={{ color: beaconColor }}>
                {beaconClientAbbr} · {beaconLOB} · {beaconDays}d
              </div>
            </div>
          )}

          {/* ---- CLIENT ACTION DRAWER — chevron handle + sliding panel ---- */}
          {selectedClientId && insuredMap[selectedClientId] && (() => {
            const ins = insuredMap[selectedClientId];
            const clientName = (ins.CompanyName || ins.Title || 'UNKNOWN').toUpperCase();
            const open = panelPinned || panelHovered;
            return (
              <>
                <button
                  ref={handleRef}
                  type="button"
                  className={'FIG-client-action-handle' + (panelPinned ? ' FIG-client-action-handle--pinned' : '')}
                  onMouseEnter={onHandleEnter}
                  onMouseLeave={onHandleLeave}
                  onClick={onHandleClick}
                  aria-label={panelPinned ? 'Close client action panel' : 'Open client action panel'}
                  aria-expanded={open}
                >
                  <span className="FIG-client-action-handle-chev" aria-hidden="true">{'\u25B8'}</span>
                </button>
                <aside
                  ref={panelRef}
                  className={'FIG-client-action-panel' + (open ? ' FIG-client-action-panel--open' : '')}
                  aria-label="Client actions"
                  aria-hidden={!open}
                  onMouseEnter={onPanelEnter}
                  onMouseLeave={onPanelLeave}
                >
                  <div className="FIG-client-action-nameplate" title={clientName}>
                    <div className="FIG-client-action-nameplate-mask" ref={namePlateMaskRef}>
                      <div
                        ref={namePlateTrackRef}
                        className={'FIG-client-action-nameplate-track'
                          + (nameOverflows ? ' FIG-client-action-nameplate-track--scroll' : '')}
                      >
                        <span className="FIG-client-action-nameplate-text">{clientName}</span>
                        {nameOverflows && (
                          <>
                            <span className="FIG-client-action-nameplate-gap">&nbsp;&nbsp;·&nbsp;&nbsp;</span>
                            <span className="FIG-client-action-nameplate-text">{clientName}</span>
                            <span className="FIG-client-action-nameplate-gap">&nbsp;&nbsp;·&nbsp;&nbsp;</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="FIG-client-action-buttons">
                    <button className="FIG-client-action-btn"
                      onClick={() => onSelectInsured && onSelectInsured(ins, { tab: 'Profile' })}>
                      PROFILE
                    </button>
                    <button className="FIG-client-action-btn"
                      onClick={() => onSelectInsured && onSelectInsured(ins, { tab: 'Policies' })}>
                      POLICIES
                    </button>
                    <button className="FIG-client-action-btn"
                      onClick={() => onSelectInsured && onSelectInsured(ins, { tab: 'ACORDs' })}>
                      ACORDS
                    </button>
                    <button
                      className={'FIG-client-action-btn' + (invoiceStep ? ' FIG-client-action-btn--active' : '')}
                      onClick={() => {
                        if (detailPolicy) {
                          setInvoiceSelectedPolicies([detailPolicy]);
                          setInvoiceStep('builder');
                        } else {
                          setInvoiceStep('selector');
                        }
                      }}>
                      INVOICE
                    </button>
                    <button className="FIG-client-action-btn FIG-client-action-btn--disabled" disabled>
                      <span>LOCATIONS</span><span className="FIG-client-action-soon">SOON</span>
                    </button>
                    <button className="FIG-client-action-btn FIG-client-action-btn--disabled" disabled>
                      <span>BUILDINGS</span><span className="FIG-client-action-soon">SOON</span>
                    </button>
                    <button className="FIG-client-action-btn FIG-client-action-btn--disabled" disabled>
                      <span>VAULT</span><span className="FIG-client-action-soon">SOON</span>
                    </button>
                  </div>
                </aside>
              </>
            );
          })()}

          {/* ---- PORTFOLIO GAUGE GRID / DETAIL VIEW ---- */}
          {detailPolicy && !detailExiting ? (
            /* ---- DETAIL VIEW — orientation strip + cockpit editor + secondary actions ---- */
            <div className="FIG-detail-view FIG-detail-view--cpe"
              onClick={(e) => { if (e.target === e.currentTarget) closeDetail(); }}>
              <button className="FIG-detail-close FIG-detail-close--cpe" onClick={closeDetail} aria-label="Close">✕</button>

              {/* ORIENTATION: solo gauge | summary | single correlating market tile */}
              <div className="FIG-cpe-orientation">
                <div className="FIG-cpe-orientation-gauge">
                  <div className="FIG-gauge-dot-anchor">
                    <AnalogGauge
                      value={daysElapsed(detailPolicy)}
                      min={0} max={totalTerm(detailPolicy)}
                      label={(detailPolicy.Carrier || 'NO CARRIER').length > 12
                        ? (detailPolicy.Carrier || '').split(/[\s,]+/)[0].toUpperCase()
                        : (detailPolicy.Carrier || 'NO CARRIER').toUpperCase()}
                      display={String(detailPolicy.daysUntilExp)}
                      subtext={detailPolicy.LOB || 'Policy'}
                      ticks={12} size="lg"
                      monthTicks={buildMonthTicks(detailPolicy)}
                      zones={[
                        { from: 0, to: totalTerm(detailPolicy) * (275 / 365), color: '#2e7d32' },
                        { from: totalTerm(detailPolicy) * (275 / 365), to: totalTerm(detailPolicy) * (335 / 365), color: '#b8860b' },
                        { from: totalTerm(detailPolicy) * (335 / 365), to: totalTerm(detailPolicy), color: '#c0392b' },
                      ]}
                      animDelay={100}
                      monthDisplay={gaugeInterior(detailPolicy).month}
                      ledValue={gaugeInterior(detailPolicy).premium}
                      ledColor={gaugeInterior(detailPolicy).color}
                      ledPulse={gaugeInterior(detailPolicy).pulse}
                    />
                    <MarketReadinessDot policy={detailPolicy}
                      isOpen={marketPopoverId === detailPolicy.id}
                      onToggle={() => setMarketPopoverId(prev => prev === detailPolicy.id ? null : detailPolicy.id)} />
                    <MessageLight policy={detailPolicy} />
                  </div>
                </div>

                <div className="FIG-cpe-orientation-summary">
                  <div className="FIG-cpe-summary-heading">{detailPolicy.LOB || 'Policy'}</div>
                  <div className="FIG-cpe-summary-rows">
                    <div className="FIG-cpe-summary-row">
                      <span className="FIG-cpe-summary-label">CARRIER</span>
                      <span className="FIG-cpe-summary-value">{detailPolicy.Carrier || '—'}</span>
                    </div>
                    <div className="FIG-cpe-summary-row">
                      <span className="FIG-cpe-summary-label">POLICY #</span>
                      <span className="FIG-cpe-summary-value">
                        {detailPolicy.PolicyNumber ? (
                          <span className="FIG-detail-link"
                            onClick={() => {
                              const ins = insuredMap[String(detailPolicy.InsuredId)];
                              if (ins && onSelectInsured) onSelectInsured(ins, { tab: 'Policies', policyId: detailPolicy.id });
                            }}>{detailPolicy.PolicyNumber}</span>
                        ) : '—'}
                      </span>
                    </div>
                    <div className="FIG-cpe-summary-row">
                      <span className="FIG-cpe-summary-label">EFFECTIVE</span>
                      <span className="FIG-cpe-summary-value">{fmtDate(detailPolicy.EffectiveDate)}</span>
                    </div>
                    <div className="FIG-cpe-summary-row">
                      <span className="FIG-cpe-summary-label">EXPIRATION</span>
                      <span className="FIG-cpe-summary-value">{fmtDate(detailPolicy.ExpirationDate)}</span>
                    </div>
                    <div className="FIG-cpe-summary-row">
                      <span className="FIG-cpe-summary-label">DAYS REMAINING</span>
                      <span className="FIG-cpe-summary-value" style={{ color: gaugeInterior(detailPolicy).color }}>
                        {detailPolicy.daysUntilExp}
                      </span>
                    </div>
                    <div className="FIG-cpe-summary-row">
                      <span className="FIG-cpe-summary-label">WRITTEN PREMIUM</span>
                      <span className="FIG-cpe-summary-value" style={{ color: '#e6a817' }}>
                        {detailPolicy.Premium ? formatCurrencyFull(detailPolicy.Premium) : '—'}
                      </span>
                    </div>
                    <div className="FIG-cpe-summary-row">
                      <span className="FIG-cpe-summary-label">STATUS</span>
                      <span className="FIG-cpe-summary-value">
                        {(() => {
                          const st = (detailPolicy.Status || 'Active').toLowerCase();
                          const cls = st === 'active' ? 'FIG-detail-badge--green'
                            : st === 'bound' ? 'FIG-detail-badge--amber' : 'FIG-detail-badge--red';
                          return <span className={`FIG-detail-badge ${cls}`}>{detailPolicy.Status || 'Active'}</span>;
                        })()}
                        {detailPolicy.AdmittedStatus === 'Admitted' && (
                          <span className="FIG-detail-badge FIG-detail-badge--green" style={{ marginLeft: 6 }}>ADMITTED</span>
                        )}
                        {detailPolicy.AdmittedStatus === 'Surplus Lines' && (
                          <span className="FIG-detail-badge FIG-detail-badge--amber" style={{ marginLeft: 6 }}>SURPLUS LINES</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="FIG-cpe-orientation-market">
                  {(() => {
                    const m = findMarketEntry(detailPolicy, marketAdmitted, marketSurplus);
                    if (!m || !m.entries || m.entries.length === 0) {
                      return <div className="FIG-cpe-market-empty">NO MARKET DATA</div>;
                    }
                    const latest = m.entries[m.entries.length - 1];
                    const rate = Number(latest.RateChange) || 0;
                    const direction = latest.Direction || 'flat';
                    if (m.type === 'Surplus') {
                      return (
                        <SurplusMarketTile
                          lob={m.lob}
                          current={rate}
                          direction={direction}
                          distData={{
                            DistReductionFlat: latest.DistReductionFlat,
                            Dist1to9: latest.Dist1to9,
                            Dist10to19: latest.Dist10to19,
                            Dist20Plus: latest.Dist20Plus,
                          }}
                          marketEntries={m.entries}
                          animDelay={0}
                          onClick={() => openMarketDetail(m.lob, 'Surplus')}
                          openBrief={openBrief}
                          setOpenBrief={setOpenBrief}
                        />
                      );
                    }
                    return (
                      <MarketTile
                        lob={m.lob}
                        current={rate}
                        direction={direction}
                        history={m.entries.map(e => e.RateChange)}
                        marketEntries={m.entries}
                        animDelay={0}
                        pulseStagger={0}
                        onClick={() => openMarketDetail(m.lob, 'Admitted')}
                      />
                    );
                  })()}
                </div>
              </div>

              {/* COCKPIT POLICY EDITOR */}
              <CockpitPolicyEditor
                policy={detailPolicy}
                insured={insuredMap[String(detailPolicy.InsuredId)]}
                getToken={getToken}
                onPolicyUpdated={(policyId, patch) => {
                  // Optimistic: patch the in-view policy so Status/eligibility buttons flip instantly.
                  setDetailPolicy(prev => (prev && String(prev.id) === String(policyId))
                    ? { ...prev, ...(patch || {}) }
                    : prev);
                  // Reconcile: refetch policies list and rebind the detail policy to the fresh record.
                  (async () => {
                    try {
                      const token = await getToken();
                      const pols = await getItems(token, FIG_LISTS.POLICIES);
                      setPolicies(pols);
                      const now = new Date(); now.setHours(0, 0, 0, 0);
                      const fresh = pols.find(p => String(p.id) === String(policyId));
                      if (fresh) {
                        const exp = parseLocalDate(fresh.ExpirationDate);
                        const days = exp ? Math.ceil((exp - now) / 86_400_000) : null;
                        setDetailPolicy({ ...fresh, daysUntilExp: days });
                      }
                    } catch (err) {
                      console.warn('[FIGRenewalPipeline] refetch after policy update failed:', err?.message);
                    }
                  })();
                }}
                onPolicyDeleted={async () => {
                  closeDetail();
                  try {
                    const token = await getToken();
                    setPolicies(await getItems(token, FIG_LISTS.POLICIES));
                  } catch (err) {
                    console.warn('[FIGRenewalPipeline] refetch after policy delete failed:', err?.message);
                  }
                }}
                onClose={closeDetail}
              />

              {/* SECONDARY ACTION ROW — POLICY / VAULT / APPLICATION / JBB / CLAUDE (backdoor) */}
              <div className="FIG-detail-actions FIG-detail-actions--secondary">
                <button className="FIG-detail-action" onClick={() => {
                  const ins = insuredMap[String(detailPolicy.InsuredId)];
                  if (ins && onSelectInsured) onSelectInsured(ins, { tab: 'Policies', policyId: detailPolicy.id });
                }}>POLICY</button>
                <button className="FIG-detail-action" onClick={async () => {
                  const ins = insuredMap[String(detailPolicy.InsuredId)];
                  if (!ins || !onSelectInsured) return;

                  // Spec-conformant path resolution (FIGVault_Spec_v1.6 via buildVaultPath).
                  // DocType resolves from Status (Rule 9); EffectiveDate is required in filename (R7).
                  const docType = statusToDocType(detailPolicy.Status);
                  const vp = buildVaultPath({
                    insured: ins,
                    policy: detailPolicy,
                    docType,
                    effectiveDate: detailPolicy.EffectiveDate,
                  });
                  const companyName = ins?.CompanyName || 'Unknown';
                  const clientRootPath = `Clients/${companyName}/`;

                  const openFolderFallback = (toast, extra = {}) => {
                    console.warn('[FIGRenewalPipeline] VAULT fallback — opening folder instead of specific PDF', {
                      client: companyName,
                      lob: detailPolicy.LOB,
                      expectedPath: vp.fullPath,
                      folderPath: vp.folderPath,
                      errors: vp.errors,
                      warnings: vp.warnings,
                      ...extra,
                    });
                    onSelectInsured(ins, {
                      tab: 'Vault',
                      vaultPath: vp.folderPath || clientRootPath,
                      vaultToast: toast,
                    });
                  };

                  // GUARD: no-carrier / unresolvable-carrier → hard stop at folder view.
                  // Never fall through to substring scan, which could match a sibling
                  // policy's PDF that shares the same policy number (e.g., #62 Quoted
                  // future-renewal sharing 46 SBM BR7GJW with #50 Active current term).
                  const hasNoCarrier = vp.errors.some(
                    e => e.code === 'NO_CARRIER' || e.code === 'CARRIER_UNRESOLVABLE'
                  );
                  if (hasNoCarrier) {
                    return openFolderFallback(
                      vp.folderPath
                        ? `No carrier on this policy yet — showing ${detailPolicy.LOB} folder`
                        : 'Policy data incomplete — showing client vault',
                      { reason: 'no-carrier', errorCodes: vp.errors.map(e => e.code) }
                    );
                  }

                  // Other fatal errors (missing LOB, missing policy #, etc.) — also folder-only.
                  if (!vp.fullPath) {
                    return openFolderFallback(
                      vp.folderPath
                        ? `No specific PDF resolvable for ${detailPolicy.LOB}`
                        : 'Policy data incomplete — showing client vault',
                      { reason: 'no-fullpath', errorCodes: vp.errors.map(e => e.code) }
                    );
                  }

                  try {
                    const token = await getToken();
                    const driveId = await ensureFIGVault(token);

                    // 1. Try exact-path lookup (spec-authoritative).
                    const encodedFull = vp.fullPath.split('/').map(encodeURIComponent).join('/');
                    const exactRes = await fetch(
                      `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${encodedFull}?$select=name,id,file,webUrl`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (exactRes.ok) {
                      const item = await exactRes.json();
                      if (item?.webUrl) return window.open(item.webUrl, '_blank');
                    }

                    // 2. Exact match missed — list LOB folder and look for carrier+policyNumber siblings
                    //    (e.g., the Binder instead of the Policy, or a Quote).
                    const encodedFolder = vp.folderPath.split('/').map(encodeURIComponent).join('/');
                    const folderRes = await fetch(
                      `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${encodedFolder}:/children`
                      + `?$select=name,id,file,size,webUrl&$top=200`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (!folderRes.ok) {
                      return openFolderFallback(
                        `No PDF filed yet for ${detailPolicy.LOB}`,
                        { stage: 'folder-fetch', status: folderRes.status }
                      );
                    }
                    const folderData = await folderRes.json();
                    const pdfs = (folderData.value || []).filter(it => it.file && /\.pdf$/i.test(it.name || ''));
                    if (pdfs.length === 0) {
                      return openFolderFallback(`No PDF filed yet for ${detailPolicy.LOB}`, { stage: 'empty-folder' });
                    }

                    // Loose match: same carrier+policyNumber prefix AND same DocType segment.
                    // DocType filter is required (v1.3 Rule 9): folders contain multiple terms
                    // with the same carrier+policyNumber, so prefix-only match is ambiguous.
                    let best = null;
                    if (vp.carrierShort) {
                      const prefix = `${vp.carrierShort}_`.toLowerCase();
                      const policyFragment = (vp.filename.split('_')[1] || '').toLowerCase();
                      const docTypeFragment = `_${docType}_`.toLowerCase();
                      best = pdfs.find(p => {
                        const n = (p.name || '').toLowerCase();
                        return n.startsWith(prefix)
                          && policyFragment && n.includes(policyFragment)
                          && n.includes(docTypeFragment);
                      });
                    }

                    if (best && best.webUrl) window.open(best.webUrl, '_blank');
                    else openFolderFallback(
                      `No PDF matching ${vp.filename} — showing ${detailPolicy.LOB} folder`,
                      { stage: 'no-name-match', pdfCount: pdfs.length, pdfNames: pdfs.map(p => p.name) }
                    );
                  } catch (err) {
                    openFolderFallback(
                      `Error looking up PDF — showing ${detailPolicy.LOB} folder`,
                      { stage: 'error', error: err?.message }
                    );
                  }
                }}>VAULT</button>
                <button className="FIG-detail-action" onClick={() => {
                  const ins = insuredMap[String(detailPolicy.InsuredId)];
                  if (ins && onSelectInsured) onSelectInsured(ins, { tab: 'Application', policyId: detailPolicy.id, policy: detailPolicy });
                }}>APPLICATION</button>
                <button className="FIG-detail-action" onClick={() => {
                  setJbbPolicyContext(buildPolicyContext(detailPolicy, insuredMap));
                  setJbbOpen(true);
                }}>JBB</button>
                <button className="FIG-detail-action" onClick={() => {
                  setClaudePromptOpen(true);
                  setClaudeCopied(false);
                }}>CLAUDE</button>
              </div>
            </div>
          ) : !selectedClientId ? (
            /* ---- HOME GAUGE GRID — three agency-level gauges ---- */
            <div className="FIG-gauge-grid FIG-gauge-grid--lg FIG-gauge-grid--home FIG-entrance-grid"
              style={{ gridTemplateColumns: 'repeat(3, auto)' }}
              key="home">

              {/* LEFT — POLICIES IN FORCE (reversed: red low, green high) */}
              <div className="FIG-gauge-grid-slot">
                <AnalogGauge
                  value={activeCount}
                  min={0} max={countMax}
                  label="POLICIES IN FORCE"
                  ticks={Math.min(countMax, 10)}
                  size="lg"
                  formatTick={(v) => Math.round(v).toString()}
                  zones={[
                    { from: 0, to: countMax * 0.33, color: '#c0392b' },
                    { from: countMax * 0.33, to: countMax * 0.66, color: '#b8860b' },
                    { from: countMax * 0.66, to: countMax, color: '#2e7d32' },
                  ]}
                  animDelay={250}
                  marqueeDelay={-4500}
                  monthDisplay={String(activeCount)}
                  subtext="ACTIVE"
                  ledValue={'$' + Math.round(totalBookPremium).toLocaleString()}
                  ledColor={(activeCount / countMax) < 0.33 ? '#ff4444' : (activeCount / countMax) < 0.66 ? '#e6a817' : '#44cc44'}
                />
              </div>

              {/* CENTER (lifted) — AGENCY REVENUE (reversed: red low, green high) */}
              <div className="FIG-gauge-grid-slot FIG-gauge-grid-slot--lifted">
                <AnalogGauge
                  value={agencyRevenue}
                  min={0} max={revMax}
                  label="AGENCY REVENUE"
                  ticks={revMax / 30_000}
                  size="lg"
                  formatTick={(v) => `$${Math.round(v / 1000)}K`}
                  zones={[
                    { from: 0, to: revMax * 0.33, color: '#c0392b' },
                    { from: revMax * 0.33, to: revMax * 0.66, color: '#b8860b' },
                    { from: revMax * 0.66, to: revMax, color: '#2e7d32' },
                  ]}
                  animDelay={200}
                  marqueeDelay={0}
                  monthDisplay="REVENUE"
                  subtext="YTD"
                  ledValue={'$' + Math.round(agencyRevenue).toLocaleString()}
                  ledColor={(agencyRevenue / revMax) < 0.33 ? '#ff4444' : (agencyRevenue / revMax) < 0.66 ? '#e6a817' : '#44cc44'}
                />
              </div>

              {/* GAUGE 3 — NEXT TO EXPIRE (unmodified standard policy gauge) */}
              {beaconPolicy && (() => {
                const p = beaconPolicy;
                const tt = totalTerm(p);
                const interior = gaugeInterior(p);
                const carrierLabel = (p.Carrier || 'NO CARRIER');
                const shortCarrier = carrierLabel.length > 12
                  ? carrierLabel.split(/[\s,]+/)[0].toUpperCase()
                  : carrierLabel.toUpperCase();
                return (
                  <div className="FIG-gauge-grid-slot" onClick={() => openDetail(p)}>
                    <AnalogGauge
                      value={daysElapsed(p)}
                      min={0} max={tt}
                      label={shortCarrier}
                      display={String(p.daysUntilExp)}
                      subtext={p.LOB || 'Policy'}
                      ticks={12} size="lg"
                      monthTicks={buildMonthTicks(p)}
                      zones={[
                        { from: 0, to: tt * (275 / 365), color: '#2e7d32' },
                        { from: tt * (275 / 365), to: tt * (335 / 365), color: '#b8860b' },
                        { from: tt * (335 / 365), to: tt, color: '#c0392b' },
                      ]}
                      animDelay={300}
                      marqueeDelay={-9700}
                      monthDisplay={interior.month}
                      ledValue={interior.premium}
                      ledColor={interior.color}
                      ledPulse={interior.pulse}
                    />
                    <MarketReadinessDot policy={p}
                      isOpen={marketPopoverId === p.id}
                      onToggle={() => setMarketPopoverId(prev => prev === p.id ? null : p.id)} />
                    <MessageLight policy={p} />
                  </div>
                );
              })()}
            </div>
          ) : (
            /* ---- STATUS BAR + GAUGE GRID ---- */
            <>
            {(() => {
              const ins = insuredMap[selectedClientId];
              const clientName = ins ? (ins.CompanyName || ins.Title || 'UNKNOWN').toUpperCase() : 'UNKNOWN';
              return (
                <div className="FIG-client-status-bar" key={'sb-' + selectedClientId}>
                  <span className="FIG-client-status-name" title={clientName}>
                    <span className="FIG-client-status-name-mask" ref={statusNameMaskRef}>
                      <span
                        ref={statusNameTrackRef}
                        className={'FIG-client-status-name-track'
                          + (statusNameOverflows ? ' FIG-client-status-name-track--scroll' : '')}
                      >
                        <span className="FIG-client-status-name-text">{clientName}</span>
                        {statusNameOverflows && (
                          <>
                            <span className="FIG-client-status-name-gap">&nbsp;&nbsp;·&nbsp;&nbsp;</span>
                            <span className="FIG-client-status-name-text">{clientName}</span>
                            <span className="FIG-client-status-name-gap">&nbsp;&nbsp;·&nbsp;&nbsp;</span>
                          </>
                        )}
                      </span>
                    </span>
                  </span>
                  <span className="FIG-client-status-sep" aria-hidden="true">—</span>
                  <button
                    type="button"
                    className={'FIG-client-status-mode FIG-client-status-mode--active'
                      + (gaugeMode === 'active' ? ' FIG-client-status-mode--lit' : '')}
                    onClick={() => setGaugeMode('active')}
                    aria-pressed={gaugeMode === 'active'}
                  >
                    <span className="FIG-client-status-led" aria-hidden="true" />
                    <span className="FIG-client-status-label">ACTIVE</span>
                  </button>
                  <button
                    type="button"
                    className={'FIG-client-status-mode FIG-client-status-mode--quoted'
                      + (gaugeMode === 'quoted' ? ' FIG-client-status-mode--lit' : '')}
                    onClick={() => setGaugeMode('quoted')}
                    aria-pressed={gaugeMode === 'quoted'}
                  >
                    <span className="FIG-client-status-led" aria-hidden="true" />
                    <span className="FIG-client-status-label">QUOTED</span>
                  </button>
                </div>
              );
            })()}
            <div className={`FIG-gauge-grid FIG-gauge-grid--${gaugeSize} FIG-entrance-grid${detailExiting ? ' FIG-gauge-grid--entering' : ''}`}
              style={{ gridTemplateColumns: `repeat(${gaugeCols}, auto)` }}
              key={selectedClientId + ':' + gaugeMode}>
              {currentPolicies.map((p, i) => {
                const tt = totalTerm(p);
                const interior = gaugeInterior(p);
                const carrierLabel = (p.Carrier || 'NO CARRIER');
                const shortCarrier = carrierLabel.length > 12
                  ? carrierLabel.split(/[\s,]+/)[0].toUpperCase()
                  : carrierLabel.toUpperCase();
                return (
                  <div key={p.id}
                    className={'FIG-gauge-grid-slot'
                      + (returnHighlight === p.id ? ' FIG-gauge-grid-slot--returned' : '')}
                    onClick={() => openDetail(p)}>
                    <AnalogGauge
                      value={daysElapsed(p)}
                      min={0} max={tt}
                      label={shortCarrier}
                      display={String(p.daysUntilExp)}
                      subtext={p.LOB || 'Policy'}
                      ticks={12}
                      size={gaugeSize === 'lg' ? 'lg' : 'sm'}
                      monthTicks={buildMonthTicks(p)}
                      zones={[
                        { from: 0, to: tt * (275 / 365), color: '#2e7d32' },
                        { from: tt * (275 / 365), to: tt * (335 / 365), color: '#b8860b' },
                        { from: tt * (335 / 365), to: tt, color: '#c0392b' },
                      ]}
                      animDelay={detailExiting ? 50 + i * 50 : 200 + i * 50}
                      marqueeDelay={-i * 1700}
                      monthDisplay={interior.month}
                      ledValue={interior.premium}
                      ledColor={interior.color}
                      ledPulse={interior.pulse}
                    />
                    <MarketReadinessDot policy={p}
                      isOpen={marketPopoverId === p.id}
                      onToggle={() => setMarketPopoverId(prev => prev === p.id ? null : p.id)} />
                    <MessageLight policy={p} />
                  </div>
                );
              })}
              {currentPolicies.length === 0 && (
                <div className="FIG-gauge-grid-empty">
                  {gaugeMode === 'quoted' ? 'NO QUOTED POLICIES' : 'No policies for selected client'}
                </div>
              )}
            </div>
            </>
          )}

          {/* ---- MARKET CONDITIONS ANNUNCIATOR STRIP ----
                Hidden while the policy detail view is open; the detail view
                renders a single correlating tile in its orientation strip instead. */}
          {!detailPolicy && marketData.length > 0 && (
            <div className="FIG-mkt-strip">
              {/* Panel screws */}
              <svg className="FIG-panel-screw" style={{top:'6px',left:'6px'}} viewBox="0 0 10 10"><PhillipsScrew sx={5} sy={5} sr={4} /></svg>
              <svg className="FIG-panel-screw" style={{top:'6px',right:'46px'}} viewBox="0 0 10 10"><PhillipsScrew sx={5} sy={5} sr={4} /></svg>
              <svg className="FIG-panel-screw" style={{bottom:'6px',left:'6px'}} viewBox="0 0 10 10"><PhillipsScrew sx={5} sy={5} sr={4} /></svg>
              <svg className="FIG-panel-screw" style={{bottom:'6px',right:'46px'}} viewBox="0 0 10 10"><PhillipsScrew sx={5} sy={5} sr={4} /></svg>

              <div className="FIG-mkt-section">
                {/* Row 1: Admitted (Ivans) — gold */}
                <div className="FIG-mkt-tiles FIG-mkt-admitted">
                  {LOB_ORDER_ADMITTED.map((lob, i) => {
                    const entries = marketAdmitted[lob] || [];
                    const latest = entries[entries.length - 1];
                    return (
                      <MarketTile
                        key={lob}
                        lob={lob}
                        current={latest ? latest.RateChange : 0}
                        direction={latest ? latest.Direction : 'flat'}
                        history={entries.map(e => e.RateChange)}
                        marketEntries={entries}
                        animDelay={1600 + i * 150}
                        pulseStagger={i * 400}
                        onClick={entries.length ? () => openMarketDetail(lob, 'Admitted') : undefined}
                      />
                    );
                  })}
                </div>
                {/* Row 2: Surplus (CRC REDY) — red */}
                <div className="FIG-mkt-tiles FIG-mkt-surplus">
                  {LOB_ORDER_SURPLUS.map((lob, i) => {
                    const entries = marketSurplus[lob] || [];
                    const latest = entries[entries.length - 1];
                    return (
                      <SurplusMarketTile
                        key={lob}
                        lob={lob}
                        current={latest ? latest.RateChange : 0}
                        direction={latest ? latest.Direction : 'flat'}
                        distData={latest ? {
                          DistReductionFlat: latest.DistReductionFlat,
                          Dist1to9: latest.Dist1to9,
                          Dist10to19: latest.Dist10to19,
                          Dist20Plus: latest.Dist20Plus,
                        } : null}
                        marketEntries={entries}
                        animDelay={2600 + i * 150}
                        onClick={entries.length ? () => openMarketDetail(lob, 'Surplus') : undefined}
                        openBrief={openBrief}
                        setOpenBrief={setOpenBrief}
                      />
                    );
                  })}
                </div>
              </div>

              {/* ---- MARKET DETAIL PANEL ---- */}
              {marketDetail && !marketDetailExiting && (() => {
                const { lob, marketType } = marketDetail;
                const isAdmitted = marketType === 'Admitted';
                const entries = isAdmitted ? (marketAdmitted[lob] || []) : (marketSurplus[lob] || []);
                if (entries.length === 0) return null;
                const latest = entries[entries.length - 1];
                const latestRate = Number(latest.RateChange) || 0;
                const accentColor = isAdmitted ? '#e6a817' : '#cc3333';
                const lobLabel = isAdmitted
                  ? (LOB_LABELS_ADMITTED[lob] || lob)
                  : (LOB_LABELS_SURPLUS[lob] || lob);
                const latestColor = tileColor(latestRate, latest.Direction || 'flat', marketType);

                // Book exposure — match policies to this market tile
                const patterns = {
                  GL: /general liability/i,
                  CommProperty: /property/i,
                  Property: /property/i,
                  CommAuto: /auto/i,
                  BOP: /business owner|bop/i,
                  WC: /worker|comp/i,
                  Umbrella: /umbrella|excess/i,
                  ExcessUmbrella: /umbrella|excess/i,
                  Cyber: /cyber/i,
                  EO: /error|e&o|professional liability/i,
                  EPL: /employment|epl/i,
                  DO: /director|d&o/i,
                };
                const pattern = patterns[lob];
                const bookPolicies = pattern
                  ? allActive.filter(p => {
                      if (!pattern.test(p.LOB || '')) return false;
                      const isPolicySurplus = (p.AdmittedStatus || '').toLowerCase().includes('surplus');
                      return isAdmitted ? !isPolicySurplus : isPolicySurplus;
                    }).sort((a, b) => (a.daysUntilExp ?? 9999) - (b.daysUntilExp ?? 9999))
                  : [];

                return (
                  <div className="FIG-market-detail">
                    <div className="FIG-market-detail-header" style={{ borderLeft: `3px solid ${accentColor}` }}>
                      <span className="FIG-market-detail-tag" style={{ color: accentColor }}>
                        {isAdmitted ? 'ADMITTED' : 'SURPLUS'}
                      </span>
                      <span className="FIG-market-detail-lob">{lobLabel}</span>
                      <span className="FIG-market-detail-rate" style={{ color: latestColor }}>
                        {latestRate > 0 ? '+' : ''}{latestRate.toFixed(2)}%
                      </span>
                      <button className="FIG-market-detail-close" onClick={closeMarketDetail}>✕</button>
                    </div>

                    {/* Trend timeline */}
                    <div className="FIG-market-detail-section">
                      <div className="FIG-market-detail-section-title">RATE TREND</div>
                      <MarketTimeline entries={entries} marketType={marketType} />
                    </div>

                    {/* Book exposure */}
                    <div className="FIG-market-detail-section">
                      <div className="FIG-market-detail-section-title">YOUR POLICIES IN THIS MARKET</div>
                      {bookPolicies.length === 0 ? (
                        <div className="FIG-market-book-empty">No policies in this market</div>
                      ) : (
                        <div className="FIG-market-book">
                          <div className="FIG-market-book-header">
                            <span style={{ flex: 2 }}>CLIENT</span>
                            <span style={{ flex: 1.5 }}>CARRIER</span>
                            <span style={{ flex: 1, textAlign: 'right' }}>PREMIUM</span>
                            <span style={{ flex: 1, textAlign: 'right' }}>RENEWS</span>
                            <span style={{ flex: 0.7, textAlign: 'right' }}>DAYS</span>
                          </div>
                          {bookPolicies.map(p => {
                            const ins = insuredMap[String(p.InsuredId)];
                            const days = p.daysUntilExp ?? 0;
                            const dColor = days <= 30 ? '#ef4444' : days <= 90 ? '#f59e0b' : '#22c55e';
                            return (
                              <div key={p.id} className="FIG-market-book-row" onClick={() => openDetail(p)}>
                                <span style={{ flex: 2 }}>{ins?.CompanyName || '—'}</span>
                                <span style={{ flex: 1.5 }}>{p.Carrier || '—'}</span>
                                <span style={{ flex: 1, textAlign: 'right' }}>{p.Premium ? formatCurrency(p.Premium) : '—'}</span>
                                <span style={{ flex: 1, textAlign: 'right' }}>{fmtDate(p.ExpirationDate)}</span>
                                <span style={{ flex: 0.7, textAlign: 'right', color: dColor, fontWeight: 700 }}>{days}d</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Label plate */}
              <div className="FIG-mkt-plate">
                <span className="FIG-mkt-plate-sub">
                  <span className="FIG-mkt-plate-conf">CONFIDENTIAL</span>
                  {' \u2014 PROPRIETARY DATA OF FRAGOS INSURANCE GROUP, LLC'}
                  {latestPublished ? ` \u00B7 UPDATED: ${latestPublished}` : ''}
                </span>
              </div>
            </div>
          )}

          </div>{/* end power-cycle wrapper */}

          {/* ---- TRIM LEVER ---- */}
          <div className={'FIG-trim-lever' + (trimPulled ? ' FIG-trim-lever--pulled' : '')}
            onClick={handleTrimReset} title="Reset instrument panel">
            <div className="FIG-trim-track">
              <div className="FIG-trim-handle">
                <div className="FIG-trim-knurl" />
                <div className="FIG-trim-knurl" />
                <div className="FIG-trim-knurl" />
              </div>
            </div>
            <div className="FIG-trim-label">RESET</div>
          </div>
        </div>
      </div>

      {/* Claude Prompt Builder (fixed overlay, independent of gauge grid ternary) */}
      {claudePromptOpen && detailPolicy && (() => {
        const ctx = buildPolicyContext(detailPolicy, insuredMap);
        if (!ctx) return null;
        const prompt = `I'm looking at a policy in my FIG Insurance system. Here's the context:

CLIENT: ${ctx.client}
POLICY NUMBER: ${ctx.policyNumber}
LINE OF BUSINESS: ${ctx.lob}
CARRIER: ${ctx.carrier}
EFFECTIVE: ${ctx.effectiveDate} to ${ctx.expirationDate}
DAYS REMAINING: ${ctx.daysRemaining}
WRITTEN PREMIUM: ${ctx.writtenPremium}
STATUS: ${ctx.status}

Context loaded. Ready when you are.`;
        return (
          <div className="FIG-claude-backdrop" onClick={() => setClaudePromptOpen(false)}>
            <div className="FIG-claude-panel" onClick={e => e.stopPropagation()}>
              <button className="FIG-claude-close" onClick={() => setClaudePromptOpen(false)}>✕</button>
              <div className="FIG-claude-title">CLAUDE PROMPT BUILDER</div>
              <div className="FIG-claude-divider" />
              <pre className="FIG-claude-prompt">{prompt}</pre>
              <button className="FIG-claude-copy" onClick={() => {
                navigator.clipboard.writeText(prompt);
                setClaudeCopied(true);
                setTimeout(() => setClaudeCopied(false), 2000);
              }}>
                {claudeCopied ? '✓ COPIED' : 'COPY TO CLIPBOARD'}
              </button>
              <div className="FIG-claude-note">Paste into Claude.ai at claude.ai</div>
            </div>
          </div>
        );
      })()}

      {/* JBB Chat with policy context */}
      {jbbOpen && (
        <div className="FIG-jbb-backdrop" onClick={() => setJbbOpen(false)}>
          <div className="FIG-jbb-panel" onClick={e => e.stopPropagation()}>
            <JarvisChat
              context={{
                nickname: 'Marc',
                today: new Date(),
                events: [],
                weekEvents: [],
                missions: {},
                roles: [],
                postIts: [],
                groceryItems: [],
                kitchenInventory: [],
                todayDinnerPlan: null,
                purchaseAverages: null,
                memoryContext: '',
                policyContext: jbbPolicyContext,
              }}
              onClose={() => { setJbbOpen(false); setJbbPolicyContext(null); }}
              onCreateEvents={() => {}}
              onAddTask={() => {}}
              onSearchEmail={() => Promise.resolve([])}
              onReadEmail={() => Promise.resolve(null)}
              onSaveAttachment={() => Promise.resolve()}
              onMoveEmail={() => Promise.resolve()}
              onSearchVault={() => Promise.resolve([])}
              onReadVaultDoc={() => Promise.resolve(null)}
              onSearchProcedures={() => Promise.resolve([])}
              onLoadProcedure={() => Promise.resolve(null)}
              onSearchWorkouts={() => Promise.resolve([])}
              onLoadWorkout={() => Promise.resolve(null)}
              onProcedureAction={() => Promise.resolve()}
              onUpdateInventory={() => Promise.resolve()}
              onUpdateDinnerOrder={() => Promise.resolve()}
              onCreateDinnerPrep={() => Promise.resolve()}
              onAddGroceryItem={() => Promise.resolve()}
              onLogPurchase={() => Promise.resolve()}
              onSearchPurchases={() => Promise.resolve([])}
              onSearchDinnerOrders={() => Promise.resolve([])}
            />
          </div>
        </div>
      )}

      {/* Invoice Flow — Selector → Builder */}
      {invoiceStep === 'selector' && (
        <PolicySelector
          policies={currentPolicies}
          title="Select Policies for Invoice"
          initialSelected={invoiceSelectedPolicies.map(p => p.id)}
          onClose={() => { setInvoiceStep(null); setInvoiceSelectedPolicies([]); }}
          onContinue={(selected) => {
            setInvoiceSelectedPolicies(selected);
            setInvoiceStep('builder');
          }}
        />
      )}
      {invoiceStep === 'builder' && (
        <InvoiceBuilder
          policies={invoiceSelectedPolicies}
          insured={insuredMap[selectedClientId]}
          getToken={getToken}
          onBack={() => setInvoiceStep('selector')}
          onClose={() => { setInvoiceStep(null); setInvoiceSelectedPolicies([]); }}
          onGenerate={(invoiceData) => {
            console.log('[Invoice] Generate:', invoiceData);
            setInvoiceStep(null);
            setInvoiceSelectedPolicies([]);
          }}
        />
      )}

      {/* ---- DETAIL LIST ---- */}
      <div className="FIG-renewal-header">
        <h2 className="FIG-renewal-title">Policy Detail</h2>
        <span className="FIG-renewal-total">
          {total} active {total === 1 ? 'policy' : 'policies'} &middot; {within180} within 180 days
        </span>
      </div>

      <DetailSection label="Needs Attention" color="#c0392b" bgDark="#5c1a14"
        policies={red} insuredMap={insuredMap} onClick={handleClick} />
      <DetailSection label="Start Renewal" color="#b8860b" bgDark="#4a3506"
        policies={yellow} insuredMap={insuredMap} onClick={handleClick} />
      <DetailSection label="On the Horizon" color="#2e7d32" bgDark="#1a3d1c"
        policies={green} insuredMap={insuredMap} onClick={handleClick} />
      <DetailSection label="Beyond 180 Days" color="#777" bgDark="#3a3a3a"
        policies={grey} insuredMap={insuredMap} onClick={handleClick} />
    </div>
  );
}

/* ========================================================================
   Detail Section — countdown bar rows grouped by client
   ======================================================================== */

function DetailSection({ label, color, bgDark, policies, insuredMap, onClick }) {
  const premium = policies.reduce((sum, p) => sum + (Number(p.Premium) || 0), 0);
  const clientGroups = groupByClient(policies, insuredMap);

  return (
    <div className="FIG-renewal-section">
      <div className="FIG-renewal-section-header" style={{ background: bgDark, borderLeftColor: color }}>
        <div className="FIG-renewal-section-header-left">
          <span className="FIG-renewal-section-label" style={{ color: '#fff' }}>{label}</span>
          <span className="FIG-renewal-section-stats">
            {policies.length} {policies.length === 1 ? 'policy' : 'policies'}
            {premium > 0 ? <> &middot; {formatCurrencyFull(premium)} at risk</> : null}
          </span>
        </div>
        <span className="FIG-renewal-count" style={{ background: 'rgba(255,255,255,0.2)', color: '#fff' }}>
          {policies.length}
        </span>
      </div>
      {policies.length === 0 ? (
        <div className="FIG-renewal-empty-zone" style={{ borderLeftColor: color + '40' }}>
          <span className="FIG-renewal-empty-icon">&#10003;</span>
          <span>No policies in this zone</span>
        </div>
      ) : (
        <div className="FIG-renewal-body">
          {clientGroups.map(group => (
            <div key={group.key} className="FIG-renewal-client-group">
              <button className="FIG-renewal-client-header"
                onClick={() => { if (group.ins && onClick) onClick(group.policies[0]); }}>
                <span className="FIG-renewal-client-name">{group.clientName}</span>
                <span className="FIG-renewal-client-badge">
                  {group.policies.length} {group.policies.length === 1 ? 'policy' : 'policies'}
                  {' \u00B7 soonest '}{group.soonest}d
                </span>
              </button>
              {group.policies.map(p => {
                const isRed = p.daysUntilExp <= 30;
                return (
                  <button key={p.id} className="FIG-renewal-row" onClick={() => onClick(p)}>
                    <span className={'FIG-renewal-days' + (isRed ? ' FIG-renewal-days--pulse' : '')}
                      style={{ color: barColor(p.daysUntilExp) }}>
                      {p.daysUntilExp}
                      <span className="FIG-renewal-days-label">days</span>
                    </span>
                    <div className="FIG-renewal-details">
                      <div className="FIG-renewal-detail-top">
                        <span className="FIG-renewal-lob">{p.LOB || 'Policy'}</span>
                        <span className="FIG-renewal-carrier">{p.Carrier || 'No Carrier'}</span>
                      </div>
                      <div className="FIG-renewal-bar-track">
                        <div className="FIG-renewal-bar-fill" style={{
                          width: Math.max(2, Math.min(100, (p.daysUntilExp / 180) * 100)) + '%',
                          background: `linear-gradient(90deg, ${barColor(p.daysUntilExp)}, ${barColor(Math.min(180, p.daysUntilExp + 20))})`,
                        }} />
                      </div>
                      <div className="FIG-renewal-detail-bottom">
                        <span className="FIG-renewal-exp">Exp {p.ExpirationDate}</span>
                        {p.Premium ? <span className="FIG-renewal-prem">{formatCurrencyFull(p.Premium)}</span> : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FIGRenewalPipeline;
